// Humanization dictionaries for the multi-strategy screener research view.
// Raw enum/reason codes come from an internal shadow collection whose shape is
// permissive; every lookup falls back to a readable transform so an unknown
// code never renders as a bare machine string or an empty cell.

// JS-1088 (round 2 FIX 2): one priced leg's identity fields, narrowed to
// exactly what displayEntryForRow's own "is this entry really priced"
// predicate and projectedDisplayExpiry/projectedDisplayStrikes need --
// structural, not the full contracts.ts leg shape.
export interface DisplayPricedLegCandidate {
  strike?: string | null;
  expiry?: string | null;
  [key: string]: unknown;
}

// Narrow shape covering what displayEntryForRow needs off an entry/row --
// intentionally structural (not the full MultiStrategyScreenEntry/Row types
// from contracts.ts) so this module has zero dependency on the contracts
// module and stays trivially unit-testable with plain fixtures.
export interface DisplayEntryCandidate {
  strikes: string[];
  // JS-1088 (round 2 FIX 2): optional -- a production CREDIT entry (a real,
  // priced bull-put/bear-call/condor) never populates the canonical
  // `strikes` field at all (only the debit pipeline does; see
  // services/apis/domains/trade_ideas/multi_strategy_screen.py's
  // `_credit_entry`), so `strikes.length > 0` alone is BLIND to every
  // priced credit. `priced_legs` is the non-canonical, per-leg fact BOTH
  // debit and credit paths populate whenever the entry has a real,
  // resolvable instrument -- see displayEntryForRow's own docstring for the
  // full "why" and the fix this corrects.
  priced_legs?: DisplayPricedLegCandidate[];
  // JS-1091: the stable per-entry identity the SERVER stamps (see
  // domains.trade_ideas.desk_idea_argument.expression_id_for_entry) --
  // present on any row produced after this ticket shipped. Optional so a
  // legacy row (no expression_id anywhere) still degrades correctly to the
  // pure client-side selection below.
  expression_id?: string | null;
  [key: string]: unknown;
}

export interface DisplayEntryRow<TEntry extends DisplayEntryCandidate = DisplayEntryCandidate> {
  entries: TEntry[];
  // JS-1091: the row-level primary-expression stamp from the named,
  // versioned SERVER-SIDE selection policy (see domains.trade_ideas
  // .desk_idea_argument.select_primary_expression_id / PRIMARY_SELECTION_
  // POLICY_VERSION) -- "first fully priced expression in fit order, stable
  // expression-id tie-break", i.e. EXACTLY this module's own pre-existing
  // client-side rule, just moved server-side and named. Optional: absent on
  // any row produced before this ticket shipped.
  primary_expression_id?: string | null;
  // JS-1091 round 3 (FIX 1): used ONLY to deduplicate the inconsistent-stamp
  // console warning (once per row_id per session, see
  // displayEntryResolution) -- never part of the selection rule itself.
  row_id?: string | null;
}

export const ARCHETYPE_LABELS: Record<string, string> = {
  bull_call_debit: "Bull Call Spread",
  bear_put_debit: "Bear Put Spread",
  long_call: "Long Call",
  long_put: "Long Put",
  bull_put_credit: "Bull Put Spread",
  bear_call_credit: "Bear Call Spread",
};

// Debit archetypes buy premium; credit archetypes sell premium.
const CREDIT_ARCHETYPES = new Set(["bull_put_credit", "bear_call_credit"]);

export function archetypeLabel(archetype: string | null | undefined): string {
  if (!archetype) return "—";
  return ARCHETYPE_LABELS[archetype] ?? humanizeToken(archetype);
}

export function premiumSide(archetype: string | null | undefined): "buy premium" | "sell premium" | null {
  if (!archetype) return null;
  return CREDIT_ARCHETYPES.has(archetype) ? "sell premium" : "buy premium";
}

// ---------------------------------------------------------------------------
// JS-1087: honest direction classification. The wire contract's `direction`
// field is `z.string().nullish()` (permissive on purpose -- see
// contracts.ts's multiStrategyScreenRowSchema), so it can legitimately arrive
// as `null`, an empty string, or any future/unrecognized value the backend
// starts emitting. Treating "anything that is not exactly BULLISH" as
// BEARISH (the pre-fix behavior in both MultiStrategyScreenTable.tsx and
// EditionPage.tsx) is a honesty violation: a null/unknown direction rendered
// as a confident, colored "BEARISH" badge and silently allowed every
// direction-dependent action to proceed as if it were a real bearish idea.
//
// `directionView` is the ONE shared three-way classifier both surfaces call
// through (NO-TECH-DEBT): exactly "BULLISH" and exactly "BEARISH" resolve to
// their own kind; every other value (null, "", "SIDEWAYS", a future enum
// member) resolves to "UNKNOWN" with the honest "direction unavailable"
// copy, never a guessed bullish/bearish default.
export type DirectionKind = "BULLISH" | "BEARISH" | "UNKNOWN";

export interface DirectionView {
  kind: DirectionKind;
  /** The exact label to render where direction text appears, e.g. in the
   * row's direction chip or the drawer head -- "BULLISH" / "BEARISH" for a
   * recognized direction, the honest "direction unavailable" otherwise. */
  label: string;
}

export const DIRECTION_UNAVAILABLE = "direction unavailable";

export function directionView(direction: string | null | undefined): DirectionView {
  if (direction === "BULLISH") return { kind: "BULLISH", label: "BULLISH" };
  if (direction === "BEARISH") return { kind: "BEARISH", label: "BEARISH" };
  return { kind: "UNKNOWN", label: DIRECTION_UNAVAILABLE };
}

export interface RegimeCopy {
  label: string;
  muted: boolean;
}

// JS-1087 (fix 5): the prior copy ("IV Cheap → buy premium" / "IV Rich →
// sell premium") read as an action recommendation, not a fact -- ADR-011
// requires this surface to state relative-vol facts only, never advice.
//
// Adversarial-review fix: an intermediate cut of this copy ("IV below/above
// realized/forecast") dropped the action verb but introduced a NEW honesty
// problem -- "forecast" claims an input the classifier does not use. The
// regime is IvRvRegime (services/apis/domains/trade_ideas/
// vol_surface_signature.py), derived SOLELY from
// ``iv_rv20_spread = atm30_iv - realized_volatility_20d`` against versioned
// Decimal thresholds -- a pure historical 20-trading-day realized-vol
// comparison, never a forward-looking forecast.
//
// Owner amendment: front-facing copy must read as trader vernacular, not
// spec/implementation wording ("trailing realized vol (20d)" reads like a
// docstring, not a trading desk). "20-day realized" is the exact same fact
// stated the way a trader says it. See the "iv_rv_regime" enum-tripwire
// group (screenerLabelsEnumFixture.test.ts) for the paired backend-vs-
// frontend completeness guard.
export function regimeCopy(regime: string | null | undefined): RegimeCopy {
  switch (regime) {
    case "IMPLIED_CHEAP":
      return { label: "IV below 20-day realized", muted: false };
    case "IMPLIED_RICH":
      return { label: "IV above 20-day realized", muted: false };
    case "BALANCED":
      return { label: "IV in line with 20-day realized", muted: false };
    case "INSUFFICIENT_DATA":
      return { label: "Vol comparison unavailable", muted: true };
    default:
      return { label: "Vol regime unknown", muted: true };
  }
}

// JS-1086 (hi-fi Desk): the row/card-level vol-read TONE for the raw
// `iv_rv_regime` string, shared by MultiStrategyScreenTable and EditionPage
// so both surfaces map the same four states to the same semantic tone
// (cheap = gain, rich = warning, balanced = default/neutral, anything else
// = muted/unknown) rather than each re-deriving it. This is presentational
// tone only -- `regimeCopy` above remains the single source of the LABEL
// text; this function does not duplicate or replace it.
export type VolRegimeTone = "cheap" | "rich" | "balanced" | "unknown";

export function volRegimeTone(regime: string | null | undefined): VolRegimeTone {
  switch (regime) {
    case "IMPLIED_CHEAP":
      return "cheap";
    case "IMPLIED_RICH":
      return "rich";
    case "BALANCED":
      return "balanced";
    default:
      return "unknown";
  }
}

export const REASON_CODE_LABELS: Record<string, string> = {
  DIRECTIONAL_TREND_BULLISH: "Bullish trend confirmed",
  DIRECTIONAL_TREND_BEARISH: "Bearish trend confirmed",
  IMPLIED_CHEAP: "Implied vol cheap vs realized",
  IMPLIED_RICH: "Implied vol rich vs realized",
  IMPLIED_BALANCED: "Implied ≈ realized vol",
  IV_RANK_UNKNOWN: "No IV history (regime from IV/RV only)",
  DEBIT_VERTICAL: "Defined-risk spread",
  LONG_CONVEXITY: "Long convexity",
  EXPECTED_MOVE_INFORMS_STRIKES: "Strikes sized to expected move",
  FLOW_CONFIRMS: "Flow confirms direction",
  FLOW_CONTRADICTS: "Flow contradicts direction",
  DIRECTION_RS_CONFLICT: "Rel. strength conflicts with direction",
  EARNINGS_SOON: "Earnings soon",
  DEBIT_ECONOMICS_NO_ELIGIBLE_EXPRESSION: "No eligible strikes at current quotes",
  DEBIT_ECONOMICS_UNAVAILABLE_LIQUIDITY: "Insufficient option liquidity",
  DEBIT_ECONOMICS_REAL_EXPRESSION: "Real priced expression",
  // Shadow / credit vocabulary emitted by the screener + shadow expression
  // pipeline. Credit ideas are internal-only (never customer-eligible); their
  // exact economics are owned by the expression pipeline, not the screener.
  CREDIT_SHADOW_EXPRESSION_NOT_CONSTRUCTED:
    "Credit spread not built at current quotes (research-only)",
  DEBIT_ECONOMICS_OWNED_BY_EXPRESSION_PIPELINE:
    "Economics priced by the expression pipeline",
  DEBIT_ECONOMICS_OWNED_BY_PIPELINE:
    "Economics priced by the expression pipeline",
  NATURAL_ENTRY_POLARITY_MISMATCH: "Quote polarity doesn't match a credit",
  NATURAL_CREDIT_NOT_BELOW_DEFINED_WIDTH: "Credit exceeds the defined-risk width",
  QUOTE_EVIDENCE_INCOMPLETE: "Incomplete quote evidence",
  MULTI_EXPIRY_LEGS_REQUIRE_ACTIVE_POSITION_MANAGEMENT:
    "Multi-expiry legs need active management",
  MULTI_EXPIRY_PAYOFF_AUTHORITY_UNAVAILABLE: "No multi-expiry payoff authority",
  EXECUTABLE_COMBO_QUOTE_AUTHORITY_UNAVAILABLE: "No executable combo-quote authority",
  BROKER_MARGIN_AUTHORITY_UNAVAILABLE: "Broker margin not modeled",
  SHORT_LEG_ASSIGNMENT_PATH_UNMODELED: "Short-leg assignment path not modeled",
  RESIDUAL_LONG_LEG_VALUE_MODEL_UNAVAILABLE: "Residual long-leg value not modeled",
  NEW_STRATEGY_FAMILY_NOT_PROMOTED: "New strategy family not yet promoted",
  ASSIGNMENT_AND_EXERCISE_POLICY_NOT_PROMOTED:
    "Assignment/exercise policy not yet promoted",
  CORPORATE_EVENT_AUTHORITY_NOT_BOUND_TO_EXPRESSION:
    "Corporate-event authority not bound",
  CONDOR_COMBO_FILL_AUTHORITY_UNAVAILABLE: "No condor combo-fill authority",
  CONDOR_BROKER_MARGIN_AUTHORITY_UNAVAILABLE: "Condor broker margin not modeled",
  CONDOR_ASSIGNMENT_AND_EX_DIVIDEND_PATHS_UNMODELED:
    "Condor assignment / ex-dividend paths not modeled",
  // DESK-009 (JS-1144): the ROW-FACE DISCLOSURES. These ride PRICED ideas --
  // they say something about a trade the desk IS showing you, never a reason
  // it was dropped -- so each reads as a caveat on a live idea, in the terms
  // a trader would use at the desk. The method is disclosed; the machinery
  // that produced it is not.
  POP_BELOW_FLOOR_CHEAP_VOL_EXCEPTION:
    "Win rate under our usual bar — here on cheap vol",
  NEGATIVE_MODEL_EV_AT_FILL: "Model says negative edge at a realistic fill",
  RICH_VOL_CORROBORATION_ESCAPE:
    "Paying up on rich vol — flow and relative strength agree",
  REGIME_UNRESOLVED: "Market regime unresolved today",
};

export function reasonCodeLabel(code: string): string {
  return REASON_CODE_LABELS[code] ?? humanizeToken(code);
}

// ---------------------------------------------------------------------------
// JS-1071: best-PRICED entry selection for display. The screener's fit-score
// ranking (topEntry in EditionPage.tsx / MultiStrategyScreenTable.tsx) picks
// the row's HERO expression for archetype/direction identity, but that
// ranking is a strategy-fit ranking, not a "did this actually price" ranking
// -- under the failsafe fit ordering (active while iv_rank is None) the
// almost-always-unpriced vertical sorts first, so the row's economics panel
// was rendering "pending" even when a sibling entry (e.g. a single-leg long
// call) priced successfully.
//
// JS-1088 (round 2, adversarial-review FIX 2): round 1's predicate --
// `strikes.length > 0` -- is BLIND to every real, priced CREDIT entry.
// services/apis/domains/trade_ideas/multi_strategy_screen.py's `_credit_entry`
// never sets the canonical `strikes`/`expiry` fields on a constructed
// SHADOW_CANDIDATE (only the debit pipeline's `_debit_entry` does); a
// production bull-put/bear-call/condor's real economics live ENTIRELY on
// the non-canonical `priced_legs` array (per-leg contract/action/strike/
// expiry) instead. A row whose only priced expression is such a credit
// therefore fell back to `entries[0]` -- typically an UNPRICED debit
// sibling -- even though a genuinely priced, resolvable structure existed
// right there on the row. "Priced" is now defined as "has a non-empty
// `strikes` array OR a non-empty `priced_legs` array" -- either canonical
// signal is sufficient; requiring `priced_legs` from EVERY caller would
// have been a breaking change for every existing debit-only fixture.
//
// `displayEntryForRow` is a PURE, display-time-only selection: it returns
// the first entry (in the row's own `entries` order, NOT re-sorted by fit)
// that satisfies this predicate. When no entry in the row priced, it falls
// back to `entries[0]` (today's behavior) so an honest abstain-reason
// headline still has a concrete entry to explain. Editions are immutable
// snapshots; this never mutates or re-ranks anything stored -- it only
// changes what a given render picks to SHOW.
export function isEntryPriced(entry: DisplayEntryCandidate): boolean {
  return (entry.strikes?.length ?? 0) > 0 || (entry.priced_legs?.length ?? 0) > 0;
}

// JS-1091 round 2 (FIX 2, stamp authority): `displayEntryResolution` treats
// the row's own server-stamped `primary_expression_id` as AUTHORITATIVE (the
// named, versioned selection policy
// `trade_idea_primary_expression_selection.first_priced_in_fit_order.v1` --
// see domains.trade_ideas.desk_idea_argument.select_primary_expression_id).
// `expression_id` is now merged onto each entry SERVER-SIDE
// (services/apis/domains/trade_ideas/screener_reads.py's `_merge_expression_ids`,
// the SAME archetype-keyed join `entry_enrichment` already uses) -- so a real,
// current row's entries always carry it, and this function never re-derives
// an id itself.
//
// JS-1091 round 3 (FIX 1): round 2's `displayEntryForRow` returned
// `undefined` for an unmatched stamp, but EVERY caller applied `?? top`
// immediately afterward -- silently substituting a GUESSED entry right back
// in and defeating the whole point of the honest-unavailable signal. This
// module now exposes a DISCRIMINATED result (`DisplayEntryResolution`) so a
// caller can render an EXPLICIT "expression data inconsistent" state instead
// of quietly falling back to a guess. `displayEntryForRow` (kept below) is a
// thin, backward-compatible unwrap for call sites/tests that only need
// "the entry or undefined" and do not render a distinct inconsistent state.
//
// Three cases, each handled distinctly (never collapsed into one silent
// fallback):
//   1. `primary_expression_id` PRESENT and it matches an entry's own
//      `expression_id` -- the common, correct case: `{status: "ok", entry}`.
//   2. `primary_expression_id` PRESENT but UNMATCHED (no entry carries that
//      id) -- this should never happen for a row produced after this
//      ticket shipped (the id is stamped FROM the row's own entries), so it
//      signals a real data inconsistency, not an expected legacy gap:
//      `{status: "inconsistent"}`. Logs a console warning (telemetry note,
//      DEDUPLICATED once per `row.row_id` per session via
//      `_warnedInconsistentRowIds` -- a row re-rendered on every keystroke/
//      re-sort must not spam the console) -- it NEVER silently falls
//      through to the pure client-side re-derivation below, which would
//      mask the inconsistency by guessing a DIFFERENT entry than the one
//      the server actually named.
//   3. `primary_expression_id` ABSENT entirely -- a genuine LEGACY row
//      (produced before this ticket shipped, so it was never stamped at
//      all). This is the ONLY case that falls back to the pure client-side
//      rule: first entry, in the row's own order, satisfying
//      `isEntryPriced`, else `entries[0]` -- proven byte-identical to the
//      server's own policy for any row that DOES carry the stamp (see
//      test_desk_idea_argument.py's "primary-selection byte-identity" test
//      and this file's own mirror test) -- returned as
//      `{status: "ok", entry}` (or `{status: "empty"}` for a genuinely
//      entry-less row).
export type DisplayEntryResolution<TEntry extends DisplayEntryCandidate> =
  | { status: "ok"; entry: TEntry }
  | { status: "inconsistent" }
  | { status: "empty" };

// Session-local, unbounded-in-theory but bounded-in-practice (one entry per
// DISTINCT inconsistent row_id actually encountered, never per render) --
// see the dedup note above.
const _warnedInconsistentRowIds = new Set<string>();

export function displayEntryResolution<TEntry extends DisplayEntryCandidate>(
  row: DisplayEntryRow<TEntry>,
): DisplayEntryResolution<TEntry> {
  const entries = row.entries ?? [];
  if (row.primary_expression_id) {
    const stamped = entries.find((entry) => entry.expression_id === row.primary_expression_id);
    if (stamped) return { status: "ok", entry: stamped };
    // Present but unmatched: an honest INCONSISTENT state, never a silent
    // re-derivation that would mask a real data inconsistency.
    const rowId = typeof row.row_id === "string" ? row.row_id : null;
    if (rowId === null || !_warnedInconsistentRowIds.has(rowId)) {
      if (rowId !== null) _warnedInconsistentRowIds.add(rowId);
      // eslint-disable-next-line no-console -- deliberate telemetry note, not debug noise.
      console.warn(
        "displayEntryResolution: primary_expression_id is present but matches no entry",
        { primary_expression_id: row.primary_expression_id, row_id: row.row_id },
      );
    }
    return { status: "inconsistent" };
  }
  // Legacy row: no stamp at all -- fall back to the pure client-side rule.
  const priced = entries.find(isEntryPriced);
  const fallback = priced ?? entries[0];
  return fallback ? { status: "ok", entry: fallback } : { status: "empty" };
}

// Backward-compatible unwrap: "the entry, or undefined for BOTH the
// inconsistent and empty cases". Kept for call sites/tests that only need a
// concrete-entry-or-nothing and do not render the distinct inconsistent
// state (see `displayEntryResolution` for the full discriminated result new
// production call sites should use instead).
export function displayEntryForRow<TEntry extends DisplayEntryCandidate>(
  row: DisplayEntryRow<TEntry>,
): TEntry | undefined {
  const resolution = displayEntryResolution(row);
  return resolution.status === "ok" ? resolution.entry : undefined;
}

// JS-1088 (round 2 FIX 2): project a display EXPIRY from `priced_legs` when
// the entry's own canonical `expiry` is absent (exactly the credit-entry
// case above -- `_credit_entry` never sets it). ALL legs of a v1-supported
// structure must share one expiry (mirrors the backend's own
// `_entry_honesty_fields`'s identical "all legs of a v1-supported structure
// share one expiry" assumption for its `structure_expiry` derivation) --
// a mismatch (legs disagreeing on expiry, which should never happen for a
// real structure) is an honest `null`, never a guessed date. Prefers the
// entry's own canonical `expiry` when present (the debit path's existing
// behavior is completely unchanged); only falls through to the
// priced_legs projection when canonical `expiry` is absent.
export function projectedDisplayExpiry(entry: DisplayEntryCandidate): string | null {
  const canonical = typeof entry.expiry === "string" && entry.expiry ? entry.expiry : null;
  if (canonical) return canonical;
  const legs = entry.priced_legs ?? [];
  const expiries = new Set(
    legs.map((leg) => (typeof leg.expiry === "string" && leg.expiry ? leg.expiry : null)).filter((value): value is string => value !== null),
  );
  return expiries.size === 1 ? [...expiries][0] : null;
}

// JS-1088 (round 2 FIX 2): project display STRIKES from `priced_legs` when
// the entry's own canonical `strikes` is empty -- the SAME credit-entry gap
// `projectedDisplayExpiry` corrects, for the row/drawer's strike display.
// Order follows `priced_legs`' own order (already a stable, meaningful leg
// order from the backend); prefers canonical `strikes` when non-empty.
export function projectedDisplayStrikes(entry: DisplayEntryCandidate): string[] {
  if ((entry.strikes?.length ?? 0) > 0) return entry.strikes;
  const legs = entry.priced_legs ?? [];
  return legs
    .map((leg) => (typeof leg.strike === "string" && leg.strike ? leg.strike : null))
    .filter((value): value is string => value !== null);
}

// ---------------------------------------------------------------------------
// Best-idea (Phase 4) axis vocabulary. The composite signal score blends
// three axes; each carries reason codes explaining its sub-score. These are
// research/internal codes, so every lookup falls back to `humanizeToken`.

export const AXIS_NAME_LABELS: Record<string, string> = {
  DIRECTIONAL_SETUP: "Setup",
  CORROBORATION: "Confirmation",
  RISK: "Risk",
};

export function axisNameLabel(name: string | null | undefined): string {
  if (!name) return "—";
  return AXIS_NAME_LABELS[name] ?? humanizeToken(name);
}

// Reason codes emitted by the three best-idea axes (setup / confirmation /
// risk). Kept permissive: unknown codes humanize gracefully.
export const AXIS_REASON_CODE_LABELS: Record<string, string> = {
  // Setup axis (regime coherence + fit).
  SETUP_REGIME_COHERENT: "Setup fits the volatility regime",
  SETUP_REGIME_PARTIAL: "Setup partially fits the regime",
  SETUP_REGIME_INCOHERENT: "Setup conflicts with the regime",
  SETUP_REGIME_INSUFFICIENT: "Not enough data to judge regime fit",
  SETUP_FIT_AT_FLOOR: "Strategy match at its floor",
  SETUP_NO_TOP_ENTRY: "No leading expression to score",
  // Confirmation (corroboration) axis.
  MOMENTUM_CONFIRMS: "Momentum confirms the direction",
  MOMENTUM_CONTRADICTS: "Momentum contradicts the direction",
  MOMENTUM_UNAVAILABLE: "Momentum evidence unavailable",
  SKEW_TERM_CONFIRMS: "Skew / term structure confirms",
  SKEW_TERM_CONTRADICTS: "Skew / term structure contradicts",
  SKEW_TERM_UNAVAILABLE: "Skew / term structure unavailable",
  // JS-1068: market-regime confirmer -- the generic label here is a
  // fallback; `confirmationAxisReasonCodeLabel` below composes the source
  // ticker in when the row's own market_regime_at_publication is available
  // (adversarial-review FIX 7: "confirmation breakdown retains source
  // provenance").
  REGIME_CONFIRMS: "Market regime confirms",
  REGIME_CONTRADICTS: "Market regime contradicts",
  REGIME_UNAVAILABLE: "Market regime unavailable",
  // Risk axis.
  MACRO_EVENT_PROXIMITY: "Near a high-impact macro event",
  MACRO_UNAVAILABLE: "Macro calendar unavailable (not penalized)",
  THIN_LIQUIDITY: "Thin option liquidity",
  LIQUIDITY_UNKNOWN: "Liquidity could not be verified",
  EARNINGS_SOON: "Earnings within the risk window",
  EARNINGS_PROXIMITY_VETO: "Earnings too close (idea vetoed)",
  LEG_SPREAD_TOO_WIDE: "Leg bid/ask spread too wide",
};

export function axisReasonCodeLabel(code: string): string {
  return AXIS_REASON_CODE_LABELS[code] ?? REASON_CODE_LABELS[code] ?? humanizeToken(code);
}

// JS-1068 (adversarial-review FIX 7): "the confirmation breakdown retains
// source provenance" -- REGIME_CONFIRMS/REGIME_CONTRADICTS/REGIME_UNAVAILABLE
// name WHICH proxy ticker backed the vote (e.g. "Market regime confirms
// (QQQ)" / "Market regime confirms (SPY fallback)") whenever the row's own
// `market_regime_at_publication.source_ticker` is available, rather than the
// generic axisReasonCodeLabel fallback. Every other axis reason code is
// unaffected -- this only augments the three regime-confirmer codes.
const REGIME_AXIS_REASON_CODES = new Set(["REGIME_CONFIRMS", "REGIME_CONTRADICTS", "REGIME_UNAVAILABLE"]);

export function confirmationAxisReasonCodeLabel(
  code: string,
  sourceTicker?: string | null,
): string {
  const base = axisReasonCodeLabel(code);
  if (!REGIME_AXIS_REASON_CODES.has(code) || !sourceTicker) return base;
  const ticker = sourceTicker.toUpperCase();
  const proxy = ticker === MARKET_REGIME_PRIMARY_TICKER ? ticker : `${ticker} fallback`;
  return `${base} (${proxy})`;
}

export const PROVENANCE_TAG_LABELS: Record<string, string> = {
  TARGET_EXPECTED_MOVE_DERIVED: "target: expected-move derived",
  INVALIDATION_ATR_DERIVED: "invalidation: ATR-derived",
};

export function provenanceTagLabel(tag: string): string {
  return PROVENANCE_TAG_LABELS[tag] ?? humanizeToken(tag);
}

// ---------------------------------------------------------------------------
// Staleness (zero-compute, client-side): derive a per-row freshness tier purely
// from `generated_at`. No backend, no market data — just an age comparison. The
// timestamp arrives ISO-ish from an internal collection (e.g.
// "2026-08-29 02:30:54.766476+00:00": space separator, microseconds, +00:00),
// so we normalize the space to 'T' before Date.parse and fail soft to "unknown"
// rather than throwing on anything unparseable.

export type StalenessTier = "fresh" | "aging" | "stale" | "unknown";

export interface Staleness {
  tier: StalenessTier;
  label: string;
  tooltip: string;
}

const FRESH_MAX_MS = 4 * 60 * 60 * 1000; // < 4h → fresh
// JS-1098 (Codex round): exported so callers that need to schedule a single
// timer at the fresh/aging → stale boundary (e.g.
// deskIdeaWatchPrefill.ts's row-age +Watch gate) can derive that instant
// directly from `generated_at + AGING_MAX_MS` without duplicating this
// threshold or reimplementing computeStaleness's own tiering.
export const AGING_MAX_MS = 24 * 60 * 60 * 1000; // 4h–24h → aging; > 24h → stale

// Best-effort parse of the ISO-ish timestamp. Returns epoch ms or null (never
// throws). Handles the space-vs-'T' separator and trims stray whitespace; JS
// Date.parse copes with microseconds and the +00:00 offset already.
export function parseGeneratedAt(value: string | null | undefined): number | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Replace the first space (date/time separator) with 'T' so it is a valid
  // ISO 8601 string; leave any other content alone.
  const normalized = trimmed.replace(" ", "T");
  const ms = Date.parse(normalized);
  return Number.isNaN(ms) ? null : ms;
}

// JS-1088: absolute local-time rendering of a row's generation instant, for
// the staleness tooltip's quote/context-timestamp fact -- the OFFENDER-badge
// system (StalenessBadge/StalenessSummaryBadge in MultiStrategyScreenTable)
// already owns every row's freshness treatment; this only ADDS the exact
// instant to that SAME tooltip rather than opening a second, competing
// timestamp display elsewhere on the row (no double-display, per the
// ticket). Uses the viewer's own local timezone (no UTC label needed -- the
// browser's `toLocaleString` already reflects it) so the fact reads exactly
// like any other on-screen clock time a trader is used to.
function absoluteQuoteTimeLabel(generatedMs: number): string {
  return new Date(generatedMs).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function computeStaleness(
  generatedAt: string | null | undefined,
  now: Date = new Date(),
): Staleness {
  const generatedMs = parseGeneratedAt(generatedAt);
  if (generatedMs === null) {
    return {
      tier: "unknown",
      label: "—",
      tooltip: "No generation timestamp on this row — freshness is unknown.",
    };
  }

  // Clamp negatives (clock skew / future timestamps) to 0 so a slightly-ahead
  // server clock still reads as fresh rather than throwing off the tiering.
  const ageMs = Math.max(0, now.getTime() - generatedMs);
  const quoteTime = absoluteQuoteTimeLabel(generatedMs);

  if (ageMs < FRESH_MAX_MS) {
    return {
      tier: "fresh",
      label: "Fresh",
      tooltip: `Quoted ${quoteTime} (${humanizeAge(ageMs)}) — within the last 4 hours.`,
    };
  }

  if (ageMs < AGING_MAX_MS) {
    return {
      tier: "aging",
      label: "Aging",
      tooltip: `Quoted ${quoteTime} (${humanizeAge(ageMs)}) — 4 to 24 hours old.`,
    };
  }

  return {
    tier: "stale",
    label: "Stale · re-check",
    tooltip: `Quoted ${quoteTime} (${humanizeAge(ageMs)}). Signals may have moved; re-run the screen to re-check.`,
  };
}

// Compact human age ("just now" / "37m ago" / "6h ago" / "2d ago") for tooltips.
function humanizeAge(ageMs: number): string {
  const mins = Math.round(ageMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

// ---------------------------------------------------------------------------
// JS-1086 review fix (HIGH): a table-summary badge that read the NEWEST row's
// timestamp silently concealed a stale/unknown-age row sitting anywhere else
// on the page -- the summary would happily say "Fresh" while a stragglers
// carried genuinely stale or missing timestamps. The honest fix is
// worst-tier-wins: the summary reflects the WORST staleness tier present
// across every row, not the best (newest) one. `unknown` (a missing or
// unparseable generated_at) ranks as the worst tier of all -- a row this
// table cannot even judge the age of is a bigger honesty gap than a row it
// knows is merely old.
const STALENESS_TIER_RANK: Record<StalenessTier, number> = {
  fresh: 0,
  aging: 1,
  stale: 2,
  unknown: 3,
};

export interface StalenessSummary {
  tier: StalenessTier;
  // Non-color signifier text for the summary badge -- "N row(s) age
  // unknown" when the worst tier is `unknown` (naming the honesty gap
  // explicitly, per the review's own required copy), otherwise the same
  // Staleness.label a single row would carry (e.g. "Stale · re-check").
  label: string;
  tooltip: string;
  // Count of rows AT the worst tier (used by the label's "N row(s)" phrasing
  // and available to a caller that wants it directly).
  worstTierCount: number;
}

/**
 * Reduce a set of rows' own `generated_at` values to ONE worst-tier summary
 * fact -- worst tier wins, `unknown` outranks `stale`. Returns null for an
 * empty input (no rows to summarize; the caller renders nothing rather than
 * a fabricated "Fresh" for a page with no data at all).
 */
export function summarizeStaleness(
  generatedAts: Array<string | null | undefined>,
  now: Date = new Date(),
): StalenessSummary | null {
  if (generatedAts.length === 0) return null;
  let worst: Staleness = computeStaleness(generatedAts[0], now);
  let worstCount = 1;
  for (let i = 1; i < generatedAts.length; i++) {
    const current = computeStaleness(generatedAts[i], now);
    const currentRank = STALENESS_TIER_RANK[current.tier];
    const worstRank = STALENESS_TIER_RANK[worst.tier];
    if (currentRank > worstRank) {
      worst = current;
      worstCount = 1;
    } else if (currentRank === worstRank) {
      worstCount += 1;
    }
  }
  const label =
    worst.tier === "unknown"
      ? `${worstCount} row${worstCount === 1 ? "" : "s"} age unknown`
      : worst.label;
  const tooltip =
    worst.tier === "unknown"
      ? `${worstCount} row${worstCount === 1 ? "" : "s"} ${worstCount === 1 ? "has" : "have"} no generation timestamp — freshness cannot be judged for ${worstCount === 1 ? "it" : "them"}.`
      : worst.tooltip;
  return { tier: worst.tier, label, tooltip, worstTierCount: worstCount };
}

// ---------------------------------------------------------------------------
// Execution-friction / implied-probability honesty fields (JS-1051/1052).
// Both groups are NON-CANONICAL and absent on entries without real priced
// legs (or, for probability, a real per-leg IV) -- every helper here is a
// pure formatter that returns null on missing/unparseable input so a caller
// can render "as today" (no dashes, no placeholders) when the fact is absent.

export type LiquidityTone = "neutral" | "warning" | "flag";

export interface LiquiditySignifier {
  tone: LiquidityTone;
  // Short non-color text signifier for warning/flag tiers (a11y: color must
  // never be the only signal) -- null for "neutral" since a calm/expected
  // value needs no callout word.
  label: string | null;
}

// Liquidity color thresholds for spread-width-pct-of-premium:
//   < 8%    -> neutral (tight enough not to call out)
//   8–15%   -> warning (amber, worth a second look) -- "wide"
//   >= 15%  -> flag (red, wide enough to change the trade's economics) -- "very wide"
const LIQUIDITY_WARNING_MIN_PCT = 8;
const LIQUIDITY_FLAG_MIN_PCT = 15;

// Strict canonical decimal string, matching the shape the backend actually
// emits (services/apis/domains/trade_ideas/screener_reads.py._decimal_to_str
// / idea_honesty.py's Decimal128 round-trip): optional leading "-", digits,
// optional ".digits". Deliberately stricter than `Number()`: rejects
// whitespace-only strings, hex ("0x10"), scientific notation ("1e5"),
// "Infinity"/"NaN", and any other JS-numeric-coercion quirk that would
// otherwise render a fabricated number for a malformed value. This is the
// SINGLE shared gate every formatter below routes through -- an invalid
// decimal string is treated as absent (render nothing), never coerced.
const CANONICAL_DECIMAL_PATTERN = /^-?\d+(?:\.\d+)?$/;

export function parseDecimalString(value: string | null | undefined): number | null {
  if (typeof value !== "string" || !CANONICAL_DECIMAL_PATTERN.test(value)) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

// Parse a decimal-string percentage (already scaled 0-100, e.g. "19.0476"
// from spread_width_pct_of_premium) into a finite number, or null.
export function parsePercentString(value: string | null | undefined): number | null {
  return parseDecimalString(value);
}

// JS-1100: 1.5 percentage points is the smallest SPY-relative spread worth a separate drawer claim.
export const WHY_NOW_RELATIVE_STRENGTH_EXCESS_MIN = 0.015;
// JS-1100: ratios at or above 0.67 remain inside the balanced call/put-volume band.
export const WHY_NOW_CALL_PUT_RATIO_LOWER_BOUND = 0.67;
// JS-1100: ratios at or below 1.5 remain inside the balanced call/put-volume band.
export const WHY_NOW_CALL_PUT_RATIO_UPPER_BOUND = 1.5;
// JS-1100: a 10% relative IV/model gap is the minimum divergence worth narrative emphasis.
export const WHY_NOW_HAR_RV_RELATIVE_DIVERGENCE_MIN = 0.1;
// JS-1100: an IV-history reading at 25 or below is materially low on its persisted 0-100 scale.
export const WHY_NOW_IV_HISTORY_LOW_MAX = 25;
// JS-1100: an IV-history reading at 75 or above is materially high on its persisted 0-100 scale.
export const WHY_NOW_IV_HISTORY_HIGH_MIN = 75;
// JS-1100: a 0.70 composite is the minimum ENERGY rating strong enough for a separate claim.
export const WHY_NOW_ENERGY_ACTIVITY_MIN = 0.7;
// JS-1101: 20th percentile is the highest trailing-year RV percentile that merits the "coiled" squeeze claim.
export const WHY_NOW_SQUEEZE_PERCENTILE_MAX = 20;
// JS-1101: earnings comparisons render to one decimal place, so differences
// below one displayed tenth must remain "in line" rather than contradicting
// two apparently equal numbers with an above/below label.
export const WHY_NOW_EVENT_MOVE_TOLERANCE_PT = 0.1;

// Liquidity tone for a spread-width-pct-of-premium value already expressed as
// a percentage (0-100 scale, e.g. "19.05" -> flag). Null input -> null tone
// (absent, never fabricated as "neutral").
export function spreadLiquidityTone(pctOfPremium: string | null | undefined): LiquidityTone | null {
  const pct = parsePercentString(pctOfPremium);
  if (pct === null) return null;
  if (pct >= LIQUIDITY_FLAG_MIN_PCT) return "flag";
  if (pct >= LIQUIDITY_WARNING_MIN_PCT) return "warning";
  return "neutral";
}

// Non-color signifier pairing the tone with a short text label ("wide" /
// "very wide") so warning/flag tiers are legible without relying on color
// alone. Null input -> null (absent, never fabricated).
export function spreadLiquiditySignifier(pctOfPremium: string | null | undefined): LiquiditySignifier | null {
  const tone = spreadLiquidityTone(pctOfPremium);
  if (tone === null) return null;
  const label = tone === "flag" ? "very wide" : tone === "warning" ? "wide" : null;
  return { tone, label };
}

// ---------------------------------------------------------------------------
// JS-1086 (hi-fi Desk): BE×ADR threshold signifier -- the row-level breakeven-
// distance-as-a-multiple-of-average-daily-range column the hi-fi design spec
// (desk-hifi/design-spec.md §2) calls for, mirroring spreadLiquiditySignifier's
// exact tone/word contract one column over: `{ tone, word }` where `word` is
// the non-color signifier text (never null for warn/flag, always null for a
// calm value) so the threshold reads even with color disabled/unavailable.
//
// Owner-signed thresholds (see design-spec.md §2 + JS-1086 ticket text):
//   ratio <  1.2  -> "ok"   (plain, no callout)
//   ratio >= 1.2  -> "warn" ("stretched"), up to (not including) 1.6
//   ratio >= 1.6  -> "flag" ("far")
// This is a PURE threshold function over an already-computed ratio; it does
// NOT compute BE×ADR itself and does not read any backend field -- no
// average-daily-range fact exists on MultiStrategyScreenEntry/Row today, so
// wiring a live column is explicitly a follow-up (design-spec.md §7 item 1
// flags the red threshold itself as needing desk/quant sign-off before that
// wiring). Null/non-finite input -> "ok" with no word (never fabricates a
// warn/flag tier for a ratio that was never computed).
export type BeAdrTone = "ok" | "warn" | "flag";

export interface BeAdrSignifier {
  tone: BeAdrTone;
  word: "stretched" | "far" | null;
}

const BE_ADR_WARN_MIN_RATIO = 1.2;
const BE_ADR_FLAG_MIN_RATIO = 1.6;

export function beAdrSignifier(ratio: number | null | undefined): BeAdrSignifier {
  if (ratio == null || !Number.isFinite(ratio)) return { tone: "ok", word: null };
  if (ratio >= BE_ADR_FLAG_MIN_RATIO) return { tone: "flag", word: "far" };
  if (ratio >= BE_ADR_WARN_MIN_RATIO) return { tone: "warn", word: "stretched" };
  return { tone: "ok", word: null };
}

// JS-1088 (round 2, adversarial-review FIX 1): the CREDIT cushion signifier
// -- the SAME `{ tone, word }` contract as beAdrSignifier one column over,
// but with INVERSE threshold direction: a SMALL ratio is the dangerous case
// for a credit's remaining cushion (the opposite of a debit's required-move
// ratio, where a LARGE ratio is dangerous). Mirrors
// domains.trade_ideas.be_adr.py's own documented v1 policy choice for these
// two constants -- a symmetric mirror of the debit-side 1.2x/1.6x bands,
// NOT independently derived, and explicitly flagged here (as in that
// backend module's docstring) for owner/quant tuning.
//
// Owner-signed-PENDING thresholds (v1, flagged for tuning):
//   ratio >= 1.2  -> "ok"   (plain, no callout -- comfortable cushion)
//   ratio <  1.2  -> "warn" ("thin"), down to (not including) 0.6
//   ratio <  0.6  -> "flag" ("very thin")
// Null/non-finite input -> "ok" with no word (never fabricates a warn/flag
// tier for a ratio that was never computed).
export interface CushionAdrSignifier {
  tone: BeAdrTone;
  word: "thin" | "very thin" | null;
}

const CUSHION_ADR_WARN_MAX_RATIO = 1.2;
const CUSHION_ADR_FLAG_MAX_RATIO = 0.6;

export function cushionAdrSignifier(ratio: number | null | undefined): CushionAdrSignifier {
  if (ratio == null || !Number.isFinite(ratio)) return { tone: "ok", word: null };
  if (ratio < CUSHION_ADR_FLAG_MAX_RATIO) return { tone: "flag", word: "very thin" };
  if (ratio < CUSHION_ADR_WARN_MAX_RATIO) return { tone: "warn", word: "thin" };
  return { tone: "ok", word: null };
}

// JS-1088 (round 2 FIX 1): the entry's own `be_adr_ratio` payload, as it
// arrives on the wire (contracts.ts's tolerant envelope shape) -- narrowed
// to exactly what the row/drawer's class-aware rendering needs.
export interface BeAdrRatioView {
  payoff_class?: string | null;
  ratio?: string | null;
  is_binding_wing?: boolean | null;
}

// A fully-resolved row-level rendering: the formatted ratio label, the tone/
// word signifier for THIS class (never assuming DEBIT_DIRECTIONAL), and a
// short qualifier suffix ("cushion" for a credit, "(near wing)" for a
// condor's binding wing) so the row's own compact cell reads unambiguously
// without a legend lookup.
export interface BeAdrRowView {
  label: string;
  tone: BeAdrTone;
  word: string | null;
  qualifier: string | null;
}

// JS-1088 (round 2 FIX 1): the ONE place a row/drawer picks which treatment
// (debit required-move vs. credit cushion) applies to an already-classified
// be_adr_ratio payload -- never a component re-deriving the class itself
// from the entry's archetype (the payload's OWN `payoff_class`, stamped by
// the kernel that actually computed the number, is authoritative). Returns
// `null` for a missing/unrecognized class or an unparseable ratio -- an
// honest absence, never a guessed treatment.
export function beAdrRowView(payload: BeAdrRatioView | null | undefined): BeAdrRowView | null {
  const ratio = parseDecimalString(payload?.ratio);
  if (ratio === null) return null;
  const label = `${ratio.toFixed(2)}x`;
  switch (payload?.payoff_class) {
    case "DEBIT_DIRECTIONAL": {
      const signifier = beAdrSignifier(ratio);
      return { label, tone: signifier.tone, word: signifier.word, qualifier: null };
    }
    case "ONE_SIDED_CREDIT": {
      const signifier = cushionAdrSignifier(ratio);
      // JS-1088 (round 2 FIX 1): a condor's binding-wing result rides the
      // SAME ONE_SIDED_CREDIT class as a standalone one-sided credit (each
      // wing is computed via the identical cushion formula) -- distinguish
      // the two ONLY by the `is_binding_wing` flag the kernel stamps on the
      // condor's own binding wing, never by re-deriving topology from the
      // archetype here.
      const qualifier = payload?.is_binding_wing ? "near wing" : "cushion";
      return { label, tone: signifier.tone, word: signifier.word, qualifier };
    }
    default:
      return null;
  }
}

// JS-1088 (round 2 FIX 1): format the entry's own `be_adr_ratio.ratio`
// decimal-string as "0.24x" (unitless multiple, two decimal places --
// matches this table's other multiple-style figures, e.g. the drawer's
// "fit 0.62"). Null on missing/unparseable input, mirroring every other
// formatter's honest-absence contract in this section. Class-agnostic
// (a bare number formatter); prefer `beAdrRowView` for row/drawer rendering,
// which also resolves the correct tone/word/qualifier for the payload's own
// class.
export function formatBeAdrRatio(ratio: string | null | undefined): string | null {
  const parsed = parseDecimalString(ratio);
  if (parsed === null) return null;
  return `${parsed.toFixed(2)}x`;
}

// Format a decimal-string per-share price as "$0.95" (absolute value, since
// mid/fill are already signed by the caller's own net convention and the
// label pairs them with "mid"/"fill" rather than a +/- sign). Null on
// missing/unparseable input.
export function formatPerShareUsd(value: string | null | undefined): string | null {
  const parsed = parseDecimalString(value);
  if (parsed === null) return null;
  return `$${Math.abs(parsed).toFixed(2)}`;
}

// "mid $0.95 · fill $1.10" -- renders only the sides that are actually
// present; returns null when NEITHER mid nor fill is available so the caller
// can skip the row entirely instead of showing an empty label.
export function midVsFillLabel(
  mid: string | null | undefined,
  fill: string | null | undefined,
): string | null {
  const midLabel = formatPerShareUsd(mid);
  const fillLabel = formatPerShareUsd(fill);
  if (midLabel == null && fillLabel == null) return null;
  if (midLabel == null) return `fill ${fillLabel}`;
  if (fillLabel == null) return `mid ${midLabel}`;
  return `mid ${midLabel} · fill ${fillLabel}`;
}

// Format a decimal-string spread-width-pct-of-premium (already 0-100 scale)
// as "19.0%". Null on missing/unparseable input.
export function formatSpreadPct(pctOfPremium: string | null | undefined): string | null {
  const pct = parsePercentString(pctOfPremium);
  if (pct === null) return null;
  return `${pct.toFixed(1)}%`;
}

// ---------------------------------------------------------------------------
// JS-1088: executable decision facts in the row -- DTE, max-loss unit, and
// the row's own quote/context timestamp for the staleness tooltip.

// Calendar days from `now` to `expiry` (an ISO date string, e.g.
// "2026-09-18"), or null when `expiry` is missing/unparseable. Deliberately
// CALENDAR days, not trading sessions -- matches every other "DTE" figure
// already published on this entry (`exit_rule.dte_floor`,
// `earnings_exit_clock.dte_floor_days`), which are the exit-policy's own
// mechanical trading-session floor, a DIFFERENT number from "how many
// calendar days until this contract expires" -- this function answers only
// the latter, the plain trader-facing "how much time is left" question.
// Never negative: an expiry that has already passed (a stale/frozen fixture,
// or a clock skew) floors at 0 rather than rendering a confusing negative
// DTE.
export function daysToExpiry(
  expiry: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (typeof expiry !== "string" || !expiry) return null;
  const expiryMs = Date.parse(`${expiry}T00:00:00Z`);
  if (Number.isNaN(expiryMs)) return null;
  const nowMidnightUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const diffDays = Math.round((expiryMs - nowMidnightUtc) / (24 * 60 * 60 * 1000));
  return Math.max(0, diffDays);
}

// "Sep 18 · 12 DTE" -- the row's expiry paired with its own day count, one
// combined fact instead of two separate cells (column-budget discipline).
// Null when `expiry` itself is missing/unparseable (an unpriced row); DTE is
// always derivable once expiry parses, so this never partially renders
// "Sep 18 · —".
export function expiryWithDteLabel(
  expiry: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (typeof expiry !== "string" || !expiry) return null;
  const dte = daysToExpiry(expiry, now);
  if (dte === null) return null;
  const parsed = new Date(`${expiry}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return null;
  const dateLabel = parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  return `${dateLabel} · ${dte} DTE`;
}

// Format the entry's own `maximum_loss_usd` (already a real per-1-contract
// USD figure -- see services/apis/domains/trade_ideas/expression_economics.py's
// `max_loss = priced.max_loss_usd * Decimal(quantity)` with quantity
// defaulting to 1) as a bare dollar amount, e.g. "$185". The "per contract"
// unit is stated ONCE by the column header/legend that renders this value,
// never repeated per-cell (column-budget discipline) -- see
// MultiStrategyScreenTable's "Max loss (1 ct)" header. Null on missing/
// unparseable input.
export function formatMaxLossPerContract(value: string | null | undefined): string | null {
  const parsed = parseDecimalString(value);
  if (parsed === null) return null;
  return `$${Math.round(Math.abs(parsed))}`;
}

// JS-1112 round 3 finding 2: render an entry's max profit HONESTLY
// distinguishing three states -- a real dollar figure, a genuinely
// UNCAPPED upside (trader vernacular, never a fabricated number), and an
// unavailable reading. `maxProfitUnbounded` comes from
// `entry.implied_probability.max_profit_unbounded` (the ONE place this
// boolean survives end to end from the calculator -- see
// domains/trade_ideas/idea_honesty.py's `ProbabilityReading`); `usdValue`
// is the entry's own canonical `maximum_profit_usd`, which is `null` for
// BOTH "genuinely unbounded" and "not resolvable" -- exactly the ambiguity
// this function exists to resolve using the boundedness flag as the
// disambiguator:
//   * `maxProfitUnbounded === true`  -> "uncapped" (NEVER a number, even if
//     a stray `usdValue` happened to be present -- an unbounded max profit
//     has no real finite figure to show).
//   * `maxProfitUnbounded === false` and a parseable `usdValue` -> the
//     dollar figure, e.g. "$185".
//   * otherwise (boundedness itself unknown, e.g. a pre-fix persisted row
//     with neither field, or a bounded structure whose dollar figure
//     didn't resolve) -> null, so the caller renders the SAME em-dash
//     every other unavailable-data cell on this table already uses --
//     "uncapped" is reserved EXCLUSIVELY for a confirmed-unbounded reading.
export function formatMaxProfitLabel(
  usdValue: string | null | undefined,
  maxProfitUnbounded: boolean | null | undefined,
): string | null {
  if (maxProfitUnbounded === true) return "uncapped";
  if (maxProfitUnbounded !== false) return null;
  const parsed = parseDecimalString(usdValue);
  if (parsed === null) return null;
  return `$${Math.round(Math.abs(parsed))}`;
}

// Format a decimal-string probability-of-profit FRACTION (0-1 scale, e.g.
// "0.521034") as a percentage "52.1%". Null on missing/unparseable/
// out-of-range input -- never fabricates a probability outside [0, 1].
export function formatImpliedProbabilityPct(fraction: string | null | undefined): string | null {
  const parsed = parseDecimalString(fraction);
  if (parsed === null || parsed < 0 || parsed > 1) return null;
  return `${(parsed * 100).toFixed(1)}%`;
}

// Format a decimal-string expected-value-per-share as a signed dollar amount,
// e.g. "-1.06" -> "-$1.06", "1.06" -> "+$1.06". Null on missing/unparseable.
export function formatEvPerShare(value: string | null | undefined): string | null {
  const parsed = parseDecimalString(value);
  if (parsed === null) return null;
  const sign = parsed > 0 ? "+" : parsed < 0 ? "-" : "";
  return `${sign}$${Math.abs(parsed).toFixed(2)}`;
}

// The EXACT tooltip copy required for the "Implied PoP" label (ADR-009 /
// ADR-011 honesty framing) -- a model-implied risk-neutral estimate, not a
// forecast and not a win rate.
export const IMPLIED_PROBABILITY_TOOLTIP =
  "Model-implied risk-neutral estimate derived from the option legs' implied volatility — not a forecast and not a win rate.";

// ---------------------------------------------------------------------------
// JS-1076 (T1b): mechanical exit rule (JS-1053) -- deterministic copy
// rendered from the structured `target_kind`/`target_value`,
// `stop_kind`/`stop_value`, `dte_floor` fields (see
// services/apis/domains/trade_ideas/exit_policy.py's module docstring: the
// backend deliberately emits ONLY structured data, never prose, so this is
// the one place the deterministic sentence is composed). Facts only (ADR-009
// / ADR-011): every number here is read straight off the stamped rule, never
// guessed or re-derived. Honest absence: an entry the exit-policy stamping
// never ran over (e.g. a shadow/unpriced entry, or a pre-JS-1053 row) has NO
// `exit_rule` group at all -- the caller renders the "not stamped" note
// instead of calling this.

// Exported (not just module-private) so the JS-1070-era enum tripwire
// (screenerLabelsEnumFixture.test.ts) can assert these keys stay in lockstep
// with exit_policy.py's TARGET_KIND_*/STOP_KIND_* constants without having to
// reverse-engineer coverage from exitRuleCopy's black-box behavior.
export const EXIT_TARGET_KIND_LABELS: Record<string, string> = {
  VALUE_MULTIPLE_OF_DEBIT_PAID: "value reaches",
  PCT_OF_MAX_PROFIT: "captures",
  PCT_OF_MAX_CREDIT_RECEIVED: "buy back at",
};

export const EXIT_STOP_KIND_LABELS: Record<string, string> = {
  VALUE_MULTIPLE_OF_DEBIT_PAID: "value falls to",
  VALUE_MULTIPLE_OF_CREDIT_RECEIVED: "buy-back cost reaches",
};

// Format an exit-rule decimal-string threshold as either a value multiple
// ("2.0x") or a percentage ("50%"), matching which comparison kind it is.
// Null on missing/unparseable input (never fabricated).
function exitThresholdLabel(kind: string | null | undefined, value: string | null | undefined): string | null {
  const parsed = parseDecimalString(value);
  if (parsed === null || !kind) return null;
  if (kind === "PCT_OF_MAX_PROFIT" || kind === "PCT_OF_MAX_CREDIT_RECEIVED") {
    return `${(parsed * 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}%`;
  }
  return `${parsed.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 2 })}x`;
}

export interface ExitRuleFacts {
  target_kind?: string | null;
  target_value?: string | null;
  stop_kind?: string | null;
  stop_value?: string | null;
  dte_floor?: number | null;
}

/**
 * One deterministic factual sentence for a stamped exit rule, e.g.
 * "Target: captures 50% of max profit. Stop: value falls to 0.5x debit paid.
 * Time floor: close by 21 DTE." Returns null when the rule's own fields
 * don't resolve to a complete sentence (an unmapped/garbled kind or a
 * malformed threshold) -- the caller falls back to the honest "not stamped"
 * note rather than rendering a partial or fabricated rule.
 */
export function exitRuleCopy(rule: ExitRuleFacts | null | undefined): string | null {
  if (!rule) return null;
  const targetPhrase = EXIT_TARGET_KIND_LABELS[rule.target_kind ?? ""];
  const targetThreshold = exitThresholdLabel(rule.target_kind, rule.target_value);
  const stopPhrase = EXIT_STOP_KIND_LABELS[rule.stop_kind ?? ""];
  const stopThreshold = exitThresholdLabel(rule.stop_kind, rule.stop_value);
  const dteFloor = typeof rule.dte_floor === "number" && Number.isFinite(rule.dte_floor) ? rule.dte_floor : null;
  if (!targetPhrase || targetThreshold === null || !stopPhrase || stopThreshold === null || dteFloor === null) {
    return null;
  }
  const targetSuffix = rule.target_kind === "PCT_OF_MAX_PROFIT"
    ? " of max profit"
    : rule.target_kind === "PCT_OF_MAX_CREDIT_RECEIVED"
      ? " of credit received"
      : " the debit paid";
  const stopSuffix = rule.stop_kind === "VALUE_MULTIPLE_OF_CREDIT_RECEIVED" ? " the credit received" : " the debit paid";
  return `Target: ${targetPhrase} ${targetThreshold}${targetSuffix}. Stop: ${stopPhrase} ${stopThreshold}${stopSuffix}. Time floor: close by ${dteFloor} DTE.`;
}

// The honest absence note for a priced row whose exit_rule group is absent
// (never a fabricated rule, never a blank cell).
export const EXIT_RULE_NOT_STAMPED = "Exit rule not stamped";

// ---------------------------------------------------------------------------
// JS-1076 (T1b): structure-vs-alternative copy -- a PURE, table-driven
// mapping from (archetype, selector reason codes) to one deterministic
// factual sentence explaining why this structure over a simpler alternative.
// Facts only (ADR-009 / ADR-011): every sentence describes a mechanical
// property of the structure (defined risk, premium paid vs. received,
// unlimited vs. bounded upside) -- never a probability, forecast, or
// recommendation. Exhaustive over the archetypes strategy_selector actually
// emits (services/apis/domains/trade_ideas/strategy_selector.py's
// StrategyArchetype: long_call / long_put / bull_call_debit / bear_put_debit
// / bull_put_credit / bear_call_credit / iron_condor). An unmapped archetype
// or a row missing the reason codes this table keys off renders NOTHING
// (honest fallback) rather than a guessed sentence.

const STRUCTURE_VS_ALTERNATIVE_COPY: Record<string, string> = {
  bull_call_debit: "Debit vertical over long call: defined risk at a fraction of the premium.",
  bear_put_debit: "Debit vertical over long put: defined risk at a fraction of the premium.",
  long_call: "Long call over a debit vertical: unlimited upside, uncapped by a short strike.",
  long_put: "Long put over a debit vertical: downside participation not capped by a short strike; payoff bounded by the underlying reaching zero.",
  bull_put_credit: "Credit vertical: collects premium instead of paying it, with defined risk on the short side.",
  bear_call_credit: "Credit vertical: collects premium instead of paying it, with defined risk on the short side.",
  iron_condor: "Iron condor: collects premium from both sides of a range-bound view, with defined risk on each wing.",
};

/**
 * One deterministic factual sentence contrasting this archetype with its
 * natural alternative, keyed purely off the archetype string
 * (`entry.archetype`) -- the SAME identifier `archetypeLabel` already reads.
 * Exhaustive over every archetype `strategy_selector` emits; an unmapped or
 * absent archetype returns null (render nothing), never a fabricated claim.
 */
export function structureVsAlternativeCopy(archetype: string | null | undefined): string | null {
  if (!archetype) return null;
  return STRUCTURE_VS_ALTERNATIVE_COPY[archetype] ?? null;
}

// ---------------------------------------------------------------------------
// JS-1076 (T1b): JS-1075 (T1a) carry-breakeven bar + scenario-grid render
// copy. Facts only -- every sentence is read straight off the structured
// carry_breakeven / scenario_grid fields (services/apis/domains/trade_ideas
// /carry_scenario.py); never a probability or forecast. `state` drives which
// sentence renders; MOVE_REQUIRED is the only state with a real ratio.
//
// Adversarial-review FIX 1 (retro): this bar's number is the DELTA-HEDGED
// gamma/theta carry threshold -- the standard vol-desk "gamma/theta carry"
// figure -- NOT the position's own unhedged directional P&L breakeven (delta
// dominates an unhedged outcome, and this kernel never touches delta at
// all). The unhedged directional P&L picture is the scenario grid's job. The
// MOVE_REQUIRED sentence below is worded as a hedged-carry FACT ("a X.XX
// daily move balances theta ... for a delta-hedged book"), never phrased as
// "what must happen for this position to pay" -- that phrasing would be
// customer-misleading since it implies the position's own breakeven.
//
// Adversarial-review FIX 2 (retro, ``carry_breakeven.v2``): the sign-quadrant
// state machine adds ``DECAY_DOMINATES`` (theta<0 AND gamma<0 -- both greeks
// work against the position, no finite move offsets it) and an
// ``adverse_move_ceiling_usd`` fact that can accompany THETA_COLLECTS
// (theta>0, gamma<0 -- collects today, but a big enough adverse move flips
// that into a loss).

export interface CarryBreakevenFacts {
  state?: string | null;
  required_daily_move_usd?: string | null;
  expected_daily_move_usd?: string | null;
  required_to_expected_ratio?: string | null;
  net_theta_dollars_per_day?: string | null;
  adverse_move_ceiling_usd?: string | null;
}

/**
 * Deterministic carry-breakeven sentence for the current `state`:
 *   - MOVE_REQUIRED: "gamma/theta carry: a 1.23x daily move balances theta
 *     ($5.71/day) for a delta-hedged book" -- the ratio + the theta cost a
 *     delta-hedged version of this structure must overcome. Deliberately NOT
 *     "needs ... to beat theta" (that phrasing reads as the position's OWN
 *     breakeven, which this number is not).
 *   - THETA_COLLECTS (no ceiling): theta collects for a delta-hedged book;
 *     no adverse move is required -- net positive theta, non-negative gamma,
 *     no breakeven move exists.
 *   - THETA_COLLECTS (with `adverse_move_ceiling_usd`): theta collects for a
 *     delta-hedged book up to the persisted daily-move ceiling; beyond that,
 *     gamma losses outpace it.
 *   - DECAY_DOMINATES: theta and gamma both work against this structure daily
 *     for a delta-hedged book -- facts only, no ratio, no fabricated number.
 *   - GAMMA_FLAT: zero net gamma means no breakeven move exists for a
 *     delta-hedged book -- theta is negative but no finite move offsets it.
 * Returns null when the state is absent/unrecognized, or MOVE_REQUIRED is
 * missing the ratio/theta facts it needs -- the caller renders nothing
 * rather than a partial or fabricated line.
 */
export function carryBreakevenCopy(carry: CarryBreakevenFacts | null | undefined): string | null {
  if (!carry) return null;
  if (carry.state === "THETA_COLLECTS") {
    const ceiling = parseDecimalString(carry.adverse_move_ceiling_usd);
    if (ceiling !== null) {
      const ceilingText = ceiling.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return `Theta collects for a delta-hedged book up to a $${ceilingText} daily move; beyond that, gamma losses outpace it.`;
    }
    return "Theta collects for a delta-hedged book: no adverse move required.";
  }
  if (carry.state === "DECAY_DOMINATES") {
    return "Theta and gamma both work against this structure daily for a delta-hedged book.";
  }
  if (carry.state === "GAMMA_FLAT") {
    return "Zero net gamma: no breakeven move exists for a delta-hedged book.";
  }
  if (carry.state === "MOVE_REQUIRED") {
    const ratio = parseDecimalString(carry.required_to_expected_ratio);
    const theta = parseDecimalString(carry.net_theta_dollars_per_day);
    if (ratio === null || theta === null) return null;
    const ratioText = ratio.toFixed(1);
    const thetaText = Math.abs(theta).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `Gamma/theta carry: a ${ratioText}x daily move balances theta ($${thetaText}/day) for a delta-hedged book.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// JS-1076 (T1b, adversarial-review fix): the macro-event masthead line.
// services/apis/scripts/run_multi_strategy_screen.py persists
// `macro_event_context` (built by
// services/apis/domains/trade_ideas/best_ideas_score.py's
// `build_macro_event_context`) as a MARKET-WIDE top-level field on EVERY row
// of a sweep -- the same snapshot gates every name, so the caller derives ONE
// sweep-level value from the rows rather than per-row. The window this line
// renders within mirrors the exact constant the RISK axis's
// `MACRO_EVENT_PROXIMITY` flag uses (`_MACRO_PROXIMITY_DAYS = 14` in
// best_ideas_score.py, `0 <= nearest_days_until <= 14` inclusive in
// `risk_macro_flag`) so the masthead line and the risk-axis penalty always
// agree on "near". Facts only (ADR-011): states the calendar fact ("FOMC
// Press Release in 2 days"), never advice or a "caution" framing.
// `nearest_days_until` is a genuine CALENDAR-day count from the FRED tool
// (EconomicCalendar.get_economic_calendar's `(event_date - today).days`),
// not a trading-session count, so this reads "days" -- matching the honest
// unit of the underlying field rather than a fabricated "sessions" framing.
export const MACRO_EVENT_PROXIMITY_DAYS = 14;

export interface MacroEventContextFacts {
  available?: boolean | null;
  nearest_release_name?: string | null;
  nearest_days_until?: number | null;
}

/**
 * One deterministic factual masthead line, e.g. "FOMC Press Release in 2
 * days" / "Consumer Price Index today". Renders only when the context is
 * available, names a release, and that release's `nearest_days_until` falls
 * within the SAME proximity window the risk axis uses
 * (`MACRO_EVENT_PROXIMITY_DAYS`, inclusive, non-negative) -- outside the
 * window, absent, or unavailable all return null (render nothing), never a
 * fabricated or stale-feeling line.
 */
export function macroEventMastheadLine(
  context: MacroEventContextFacts | null | undefined,
): string | null {
  if (!context || context.available !== true) return null;
  const name = context.nearest_release_name;
  const days = context.nearest_days_until;
  if (!name || typeof days !== "number" || !Number.isFinite(days)) return null;
  if (days < 0 || days > MACRO_EVENT_PROXIMITY_DAYS) return null;
  const when = days === 0 ? "today" : days === 1 ? "in 1 day" : `in ${days} days`;
  return `${name} ${when}`;
}

// ---------------------------------------------------------------------------
// JS-1068: the market-wide V4.2 trend-regime line (services/apis/domains/
// trade_ideas/market_regime.py's `MarketRegimeSnapshot`). MA-comparison FACT
// only (ADR-009/ADR-011) -- never a forecast: states the classifier's label
// and how many consecutive sessions the close has held above its short EMA,
// exactly what the ticket's "Market: BULL · 14 sessions above EMA20" example
// shows. Renders nothing on an absent/malformed snapshot.
//
// Adversarial-review FIX 7: the line NAMES the signal proxy explicitly
// ("Market (QQQ): ...") rather than a bare "Market: ..." -- the label is a
// fact about ONE specific index's closes, and hiding which one invites a
// reader to assume it is always QQQ. When the resolver fell back to SPY
// (see run_multi_strategy_screen.py's `_resolve_live_market_regime`), the
// line carries a VISIBLE "(SPY fallback)" designation so a reader can tell
// the primary signal was unavailable that day, never silently swapped in
// looking identical to a QQQ reading.
// ---------------------------------------------------------------------------

// Exported so the enum tripwire (screenerLabelsEnumFixture.test.ts) can
// assert these keys stay exhaustive over market_regime.py's
// MarketRegimeLabel without needing a second, market-regime-specific
// black-box probe of marketRegimeMastheadLine.
export const MARKET_REGIME_SHORT_LABELS: Record<string, string> = {
  STRONG_BULL: "STRONG BULL",
  BULL: "BULL",
  TRANSITION: "TRANSITION",
  BEAR_WARNING: "BEAR WARNING",
  CONFIRMED_BEAR: "CONFIRMED BEAR",
  PERSISTENT_BEAR: "PERSISTENT BEAR",
  RECOVERY_ABOVE_EMA20: "RECOVERY (above EMA20)",
  RECOVERY_ABOVE_EMA50: "RECOVERY (above EMA50)",
};

// The PRIMARY signal index -- any other resolved `source_ticker` (today,
// only the documented SPY fallback) gets the visible "(SPY fallback)" /
// "(<TICKER> fallback)" designation rather than being silently indistinct
// from a QQQ reading.
const MARKET_REGIME_PRIMARY_TICKER = "QQQ";

export interface MarketRegimeMastheadFacts {
  label?: string | null;
  source_ticker?: string | null;
  recovery_above_ema20_streak?: number | null;
  recovery_above_ema50_streak?: number | null;
}

/**
 * One deterministic factual masthead line, e.g. "Market (QQQ): BULL · 14
 * sessions above EMA20" or "Market (SPY fallback): CONFIRMED BEAR". Pure and
 * total: an absent, unrecognized, or malformed snapshot renders null
 * (nothing), never a fabricated or stale-feeling line. `source_ticker` is
 * REQUIRED for the line to render at all (adversarial-review FIX 7: a
 * regime fact with no named proxy is not a renderable fact) -- the schema
 * already enforces this as mandatory, but this function re-checks it
 * directly since it may also be called with a hand-built facts object.
 * The trailing streak clause appears only for the two RECOVERY labels (the
 * streak that IS the recovery fact); every other label renders the bare
 * "Market (<PROXY>): <LABEL>" clause.
 */
export function marketRegimeMastheadLine(
  snapshot: MarketRegimeMastheadFacts | null | undefined,
): string | null {
  if (!snapshot || !snapshot.label || !snapshot.source_ticker) return null;
  const label = MARKET_REGIME_SHORT_LABELS[snapshot.label] ?? humanizeToken(snapshot.label);
  const ticker = snapshot.source_ticker.toUpperCase();
  const proxy = ticker === MARKET_REGIME_PRIMARY_TICKER ? ticker : `${ticker} fallback`;
  if (snapshot.label === "RECOVERY_ABOVE_EMA20" || snapshot.label === "RECOVERY_ABOVE_EMA50") {
    const streak =
      snapshot.label === "RECOVERY_ABOVE_EMA20"
        ? snapshot.recovery_above_ema20_streak
        : snapshot.recovery_above_ema50_streak;
    const ema = snapshot.label === "RECOVERY_ABOVE_EMA20" ? "EMA20" : "EMA50";
    if (typeof streak === "number" && Number.isFinite(streak) && streak > 0) {
      return `Market (${proxy}): ${label} · ${streak} session${streak === 1 ? "" : "s"} above ${ema}`;
    }
  }
  return `Market (${proxy}): ${label}`;
}

// ---------------------------------------------------------------------------
// JS-1077 (T2a): event-vol (implied straddle move vs historical earnings
// distribution) + the event-window x exit-clock fact. Facts only (ADR-011):
// every sentence states a descriptive number straight off the structured
// fields (services/apis/domains/trade_ideas/event_vol.py) -- NEVER
// "overpriced"/"cheap"/"sell it"/any directional or advisory language, even
// though the ratio between the two numbers is exactly the kind of figure a
// less careful implementation might phrase as a trade call.

export interface ImpliedEventMoveFacts {
  implied_move_pct?: string | null;
  front_expiry?: string | null;
  days_to_expiry?: number | null;
}

export interface HistoricalEarningsMoveDistributionFacts {
  median_abs_move_pct?: string | null;
  sample_size?: number | null;
}

export interface EventVolFacts {
  implied_move?: ImpliedEventMoveFacts | null;
  historical_distribution?: HistoricalEarningsMoveDistributionFacts | null;
  implied_to_historical_ratio?: string | null;
}

/**
 * One deterministic factual sentence contrasting the implied event move with
 * the historical distribution, e.g. "Priced ±7.2% vs 4.1% median realized
 * (4 quarters) -- 1.76×." Renders whichever half(s) resolved:
 *   - both present: "Priced ±X% vs Y% median realized (N quarters) -- Rx."
 *     (ratio suffix only when `implied_to_historical_ratio` is present too,
 *     otherwise the sentence simply ends after the quarter count);
 *   - implied only: "Priced ±X% for the move." (no historical comparison);
 *   - historical only: "Y% median realized move over N quarters." (no
 *     implied reading);
 *   - neither: null (render nothing).
 * Never states "rich"/"cheap"/"overpriced"/"sell" -- the ratio is reported as
 * a bare multiple, letting the reader draw their own conclusion (ADR-011).
 */
export function eventVolCopy(eventVol: EventVolFacts | null | undefined): string | null {
  if (!eventVol) return null;
  const impliedPct = parseDecimalString(eventVol.implied_move?.implied_move_pct);
  const medianPct = parseDecimalString(eventVol.historical_distribution?.median_abs_move_pct);
  const sampleSize = eventVol.historical_distribution?.sample_size;
  const ratio = parseDecimalString(eventVol.implied_to_historical_ratio);

  const impliedText = impliedPct !== null ? `Priced ±${impliedPct.toFixed(1)}%` : null;
  const historicalText =
    medianPct !== null && typeof sampleSize === "number" && sampleSize > 0
      ? `${medianPct.toFixed(1)}% median realized (${sampleSize} quarter${sampleSize === 1 ? "" : "s"})`
      : null;

  if (impliedText && historicalText) {
    const ratioSuffix = ratio !== null ? ` -- ${ratio.toFixed(2)}×.` : ".";
    return `${impliedText} vs ${historicalText}${ratioSuffix}`;
  }
  if (impliedText) return `${impliedText} for the move.`;
  if (historicalText) return `${historicalText.charAt(0).toUpperCase()}${historicalText.slice(1)} move.`;
  return null;
}

// The honest absence note for a catalyst-window entry whose event_vol group
// is absent (never a fabricated reading, never a blank cell).
export const EVENT_VOL_NOT_AVAILABLE = "Event pricing not available";

export interface EarningsExitClockFacts {
  outcome?: string | null;
  sessions_to_earnings?: number | null;
  dte_floor_days?: number | null;
  dte_floor_session_date?: string | null;
  earnings_session_date?: string | null;
}

/**
 * One deterministic factual sentence for the event-window x exit-clock fact.
 *
 * Adversarial-review FIX 5: the pre-fix copy ("Holds through earnings")
 * asserted more than the stamped clock actually proves -- it reads as a
 * promise that the POSITION survives to the print, when all the clock
 * itself establishes is a SCHEDULE fact: the 21-DTE mechanical time exit
 * falls on/after the earnings session.  A target or stop can still fire
 * earlier and close the position well before that floor date ever arrives.
 * The copy therefore states only the schedule fact:
 *   - EXITS_BEFORE_EARNINGS: "21-DTE time exit falls before earnings (E-N)."
 *   - HOLDS_THROUGH_EARNINGS, floor SESSION strictly after earnings SESSION:
 *     "21-DTE time exit falls after earnings (E-N). Target/stop may exit
 *     sooner." -- the mechanical qualifier is a fact of the stamped rule (a
 *     target/stop check always runs ahead of the time floor), so it earns
 *     its place here.
 *   - HOLDS_THROUGH_EARNINGS, floor SESSION equal to earnings SESSION (the
 *     documented tie -- round 2 remediation FIX 2): "21-DTE time exit and
 *     earnings fall on the same session (E-N). Target/stop may exit sooner."
 *     The backend's tie-break folds this into HOLDS_THROUGH_EARNINGS (see
 *     `event_vol.compute_earnings_exit_clock`'s docstring) because the
 *     mechanical exit fires at END-OF-DAY while an earnings print can land
 *     any time that same session -- including AFTER the exit's own close.
 *     Asserting "falls after earnings" on the tie claims a fact nobody
 *     verified (an AMC print IS after an end-of-session exit, but a BMO or
 *     intraday print is not) -- only "same session" is honestly established,
 *     so that is all this variant claims.  Distinguishing the tie needs BOTH
 *     `dte_floor_session_date` and `earnings_session_date` (the `outcome`
 *     enum alone collapses tie and strictly-after to the same value) -- both
 *     ride as siblings in the SAME persisted `earnings_exit_clock` group
 *     already (`EarningsExitClock.as_extra_fields()`), so no new backend
 *     field was needed for this fix.  When either date is missing, this
 *     falls back to "21-DTE time exit falls on or after the earnings
 *     session (E-N). Target/stop may exit sooner." -- the backend ALWAYS
 *     stamps both dates alongside `outcome` (see `EarningsExitClock`, two
 *     non-optional fields), so a HOLDS_THROUGH group missing either one is
 *     malformed/partial data, not a legitimate absence.  With no dates to
 *     compare, the tie can no longer be ruled out, so the strong "falls
 *     after earnings" claim is NOT provable -- "on or after" is the
 *     strongest claim the remaining facts (`outcome`, `sessions_to_earnings`)
 *     actually support.  The before-earnings variant is unaffected: it never
 *     depended on the two session dates in the first place.
 * Internal enum values (`outcome`) are UNCHANGED by this fix -- only the
 * customer-facing sentence changed.  `N` is `sessions_to_earnings` (trading
 * sessions to the print at generation time).  The DTE figure is read ONLY
 * from the stamped `dte_floor_days` -- round 2 remediation: a caller that
 * omits it gets NO rendered copy at all (not a fabricated "21-DTE" default).
 * The backend always stamps `dte_floor_days` alongside `outcome` (see
 * `EarningsExitClock`, a non-optional field), so a real persisted group
 * missing it is malformed data, not a legitimate absence -- honest silence
 * is the correct response, not a guessed constant that could go stale the
 * day the house DTE floor ever changes.  Returns null for an unrecognized/
 * absent outcome, a missing session count, or a missing DTE floor -- the
 * caller renders nothing rather than a partial or fabricated claim.
 */
// Round 3 remediation (+ follow-up): parse a stamped `YYYY-MM-DD` session
// date into a comparable epoch-days integer, STRICT about both SHAPE and
// CALENDAR VALIDITY.
//
// Shape: rejects anything that is not exactly 4-2-2 digits with no
// surrounding characters -- a full ISO timestamp, an empty string, or
// whitespace padding.  No `.trim()` here: this is an exact-shape contract,
// so a leading/trailing space is malformed input, not a formatting nuance
// to tolerate.
//
// Calendar validity: `Date.parse`/the `Date` constructor NORMALIZE
// out-of-range components instead of rejecting them -- `2026-02-30` silently
// becomes March 2nd, `2027-02-29` (2027 is not a leap year) silently rolls
// into March 1st, and month `13` rolls into the next January.  A
// shape-valid but calendar-IMPOSSIBLE date parsed that way would still
// produce a comparable timestamp, letting a malformed floor date reach the
// strong "falls after earnings" claim.  Guard against this by ROUND-TRIPPING:
// parse the three components as plain integers, construct the UTC date, and
// require its own UTC year/month/day getters to echo the exact components
// parsed -- any normalization means the input was never a real calendar
// date, and this returns null exactly as it would for a missing/malformed
// one.
function _parseSessionDate(value: string | null | undefined): number | null {
  if (typeof value !== "string") return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const ms = Date.UTC(year, month - 1, day);
  if (Number.isNaN(ms)) return null;
  const roundTripped = new Date(ms);
  const matches =
    roundTripped.getUTCFullYear() === year &&
    roundTripped.getUTCMonth() === month - 1 &&
    roundTripped.getUTCDate() === day;
  return matches ? ms : null;
}

export function earningsExitClockCopy(clock: EarningsExitClockFacts | null | undefined): string | null {
  if (!clock) return null;
  const sessions = clock.sessions_to_earnings;
  if (typeof sessions !== "number" || !Number.isFinite(sessions)) return null;
  if (typeof clock.dte_floor_days !== "number" || !Number.isFinite(clock.dte_floor_days)) {
    return null;
  }
  const dteFloor = clock.dte_floor_days;
  if (clock.outcome === "EXITS_BEFORE_EARNINGS") {
    return `${dteFloor}-DTE time exit falls before earnings (E-${sessions}).`;
  }
  if (clock.outcome === "HOLDS_THROUGH_EARNINGS") {
    const onOrAfterFallback = `${dteFloor}-DTE time exit falls on or after the earnings session (E-${sessions}). Target/stop may exit sooner.`;
    // Round 3 remediation: comparing the RAW strings (as round 2 did) treats
    // any unequal pair as "strictly after" -- including a MALFORMED string
    // and a CONTRADICTORY pair where the floor session is actually BEFORE
    // the earnings session (which a HOLDS_THROUGH row should never carry,
    // but this display layer must never trust an upstream invariant it
    // cannot itself verify).  Parse both as real dates and compare their
    // VALUES: only a floor STRICTLY AFTER earnings (as parsed dates) earns
    // the strong "falls after" claim; equal parsed dates is the same-session
    // tie; anything else -- either date missing/unparseable, or a
    // contradictory floor-before-earnings ordering -- falls back to the
    // defensible "on or after" reading, which is the strongest claim the
    // remaining facts (`outcome`, `sessions_to_earnings`) actually support.
    const floorMs = _parseSessionDate(clock.dte_floor_session_date);
    const earningsMs = _parseSessionDate(clock.earnings_session_date);
    if (floorMs === null || earningsMs === null) {
      return onOrAfterFallback;
    }
    if (floorMs === earningsMs) {
      return `${dteFloor}-DTE time exit and earnings fall on the same session (E-${sessions}). Target/stop may exit sooner.`;
    }
    if (floorMs > earningsMs) {
      return `${dteFloor}-DTE time exit falls after earnings (E-${sessions}). Target/stop may exit sooner.`;
    }
    // floorMs < earningsMs: contradictory for a HOLDS_THROUGH row -- never
    // assert "after" on data that says the opposite.
    return onOrAfterFallback;
  }
  return null;
}

// ---------------------------------------------------------------------------
// JS-1078 (T2b): OI walls / strike-positioning context. Facts only
// (ADR-011): every sentence states an OI-CONCENTRATION FACT straight off the
// structured fields (services/apis/domains/trade_ideas/oi_walls.py) --
// walls are OI facts, not dealer-direction claims. This module NEVER emits
// "resistance"/"support"/"pinning"/"dealer positioning"/any
// market-microstructure interpretation of what a wall means for price
// action -- only the concentration and the leg's distance from it.

export interface OiWallProximityFacts {
  is_near_wall?: boolean | null;
  nearby_wall_strike?: string | null;
  nearby_wall_open_interest?: string | null;
  // Adversarial-review FIX 2: whether the referenced wall is (tied for) the
  // MAXIMUM-OI wall in this reading -- drives the "largest" vs. generic
  // wording below.
  nearby_wall_is_largest?: boolean | null;
  // Adversarial-review FIX 3: the backend only ever emits `0`, `1`, or
  // `null`/`undefined` here (see oi_walls.py's `compute_leg_wall_proximity`)
  // -- typed permissively as `number` (not the strict zod literal union,
  // since this interface also has to accept whatever a caller/test hands
  // it), but the copy function below treats ANYTHING other than exactly `0`
  // or `1` as "no sentence," never guessing at what a `2`/`7`/other value
  // would mean.
  distance_in_strikes?: number | null;
}

/**
 * One deterministic factual sentence for a leg's wall-proximity annotation.
 *
 * Adversarial-review FIX 2: the "largest" superlative is reserved for the
 * genuine (tied-for-)maximum wall (`nearby_wall_is_largest === true`) --
 * e.g. "Strike 220 sits on the largest near-tenor OI shelf (18.4k)." A
 * lesser, but still threshold-clearing, wall gets the honest generic
 * phrasing instead -- e.g. "Strike 210 sits one strike from an OI shelf
 * (5.0k)." -- never claiming a superlative the data doesn't support.
 *
 * Adversarial-review FIX 3: exactly two adjacency branches --
 * `distance_in_strikes === 0` ("sits on") and `=== 1` ("sits one strike
 * from"). Any OTHER value (`2`, `null`, missing, a non-integer) returns
 * `null` -- NO sentence is rendered rather than fabricating an adjacency
 * claim ("one strike from") for a leg that is actually two-or-more strikes
 * away, or for a malformed/absent distance.
 *
 * ``legLabel`` names the leg for the sentence (e.g. "Strike 220", or a
 * caller with richer context may pass "Short strike 220") -- this function
 * does not know the leg's own action/strike text, only the proximity facts,
 * so the caller supplies it. Returns null when `is_near_wall` is not
 * `true`, the wall strike/OI facts don't resolve, or `distance_in_strikes`
 * is anything other than exactly `0` or `1` -- the caller renders nothing
 * (never a fabricated proximity or superlative claim).
 */
export function oiWallProximityCopy(
  legLabel: string,
  proximity: OiWallProximityFacts | null | undefined,
): string | null {
  if (!proximity || proximity.is_near_wall !== true) return null;
  const wallStrike = parseDecimalString(proximity.nearby_wall_strike);
  const wallOi = parseDecimalString(proximity.nearby_wall_open_interest);
  if (wallStrike === null || wallOi === null) return null;
  const distance = proximity.distance_in_strikes;
  let proximityPhrase: string;
  if (distance === 0) {
    proximityPhrase = "sits on";
  } else if (distance === 1) {
    proximityPhrase = "sits one strike from";
  } else {
    // FIX 3: any other distance (2, null, missing, non-integer) is honestly
    // not a claim this function can make -- render nothing.
    return null;
  }
  const oiText = formatOpenInterestCompact(wallOi);
  const shelfPhrase = proximity.nearby_wall_is_largest === true ? "the largest near-tenor OI shelf" : "an OI shelf";
  return `${legLabel} ${proximityPhrase} ${shelfPhrase} (${oiText}).`;
}

// Compact OI magnitude formatting, e.g. 18400 -> "18.4k", 950 -> "950",
// 1200000 -> "1.2m". Never rounds to "0k" for a sub-1000 value (falls back
// to the bare integer) so a real, non-zero OI reading never displays as
// zero.
function formatOpenInterestCompact(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}m`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return `${Math.round(value)}`;
}

export interface OiWallStrikeFacts {
  strike?: string | null;
  open_interest?: string | null;
  volume?: string | null;
  distance_from_spot?: string | null;
  is_wall?: boolean | null;
  // Adversarial-review FIX 2: (tied-for-)maximum-OI wall in this reading.
  is_largest_wall?: boolean | null;
}

export interface OiWallsFacts {
  tenor_expiry?: string | null;
  median_strike_open_interest?: string | null;
  strikes?: OiWallStrikeFacts[] | null;
}

// One bar-strip row per strike, pre-sorted by strike ascending (a natural
// left-to-right reading order for a bar chart, distinct from the backend's
// own OI-descending storage order) with a compact OI label and the
// `is_wall`/`is_largest_wall` flags threaded through for the component's
// own tone styling. Returns an empty array (render nothing) when the group
// is absent or carries zero strikes -- never a fabricated bar.
export interface OiWallBarRow {
  strike: string;
  openInterest: number;
  openInterestLabel: string;
  isWall: boolean;
  isLargestWall: boolean;
  distanceFromSpot: number | null;
}

export function oiWallBarRows(walls: OiWallsFacts | null | undefined): OiWallBarRow[] {
  const strikes = walls?.strikes ?? [];
  const rows: OiWallBarRow[] = [];
  for (const row of strikes) {
    const strike = parseDecimalString(row.strike);
    const oi = parseDecimalString(row.open_interest);
    if (strike === null || oi === null) continue;
    rows.push({
      strike: strike.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      openInterest: oi,
      openInterestLabel: formatOpenInterestCompact(oi),
      isWall: row.is_wall === true,
      isLargestWall: row.is_largest_wall === true,
      distanceFromSpot: parseDecimalString(row.distance_from_spot),
    });
  }
  return rows.sort((a, b) => parseDecimalString(a.strike)! - parseDecimalString(b.strike)!);
}

// The honest absence note for a priced entry whose oi_walls group is absent
// -- never a fabricated wall reading, never a blank strip.
export const OI_WALLS_NOT_AVAILABLE = "OI wall data not available";

// ---------------------------------------------------------------------------
// JS-1079 (T2c): HAR-RV realized-vol model estimate vs implied (variance risk
// premium v1) -- read verbatim from
// domains.trade_ideas.har_rv.HarRvForecast.as_extra_fields(). This is a
// MODEL ESTIMATE (ADR-009/011), never a probability, never advice, and NEVER
// "cheap"/"rich"/"overpriced"/"sell"/"buy" language -- the copy states the
// two numbers side by side plus the model's own disclosed confidence
// (method + fit R^2 + sample size) and lets the reader draw their own
// conclusion, exactly like eventVolCopy's ratio-not-verdict posture above.
// ---------------------------------------------------------------------------

export interface HarRvFacts {
  method?: string | null;
  rv_forecast_21d_annualized?: string | null;
  atm_implied_reference?: string | null;
  variance_risk_premium?: string | null;
  fit_r_squared?: string | null;
  sample_size?: number | null;
}

/**
 * One deterministic comparison line for the HAR-RV model estimate, e.g.
 * "IV 38.2% vs HAR-RV 42.5% (R² 0.61, n=240)."
 *
 * Renders ONLY when both `atm_implied_reference` and
 * `rv_forecast_21d_annualized` resolve and their relative divergence clears
 * `WHY_NOW_HAR_RV_RELATIVE_DIVERGENCE_MIN`. A lone half or quiet comparison
 * is not material drawer evidence. Both are stored as annualized-volatility
 * ratios and display as percentages. Fit and sample facts remain optional.
 *
 * NEVER states "cheap"/"rich"/"overpriced"/"sell"/"buy" or any probability/
 * recommendation language (ADR-009/011) -- see this function's own guard
 * test in screenerLabels.test.ts. Returns null when either headline number
 * is missing/malformed or the comparison is below threshold.
 */
export function harRvCopy(harRv: HarRvFacts | null | undefined): string | null {
  if (!harRv) return null;
  const implied = parseDecimalString(harRv.atm_implied_reference);
  const estimate = parseDecimalString(harRv.rv_forecast_21d_annualized);
  if (implied === null || estimate === null || estimate <= 0) return null;
  const relativeDivergence = Math.abs(implied - estimate) / estimate;
  if (relativeDivergence + Number.EPSILON < WHY_NOW_HAR_RV_RELATIVE_DIVERGENCE_MIN) return null;

  const impliedPct = (implied * 100).toFixed(1);
  const estimatePct = (estimate * 100).toFixed(1);
  const rSquared = parseDecimalString(harRv.fit_r_squared);
  const sampleSize = harRv.sample_size;

  const evidence: string[] = [];
  if (rSquared !== null) evidence.push(`R² ${rSquared.toFixed(2)}`);
  if (typeof sampleSize === "number" && sampleSize > 0) evidence.push(`n=${sampleSize}`);

  return `IV ${impliedPct}% vs HAR-RV ${estimatePct}%${evidence.length > 0 ? ` (${evidence.join(", ")})` : ""}.`;
}

/** The separate trader-meaning line for a material persisted variance risk premium. */
export function harRvVrpCopy(harRv: HarRvFacts | null | undefined): string | null {
  if (harRvCopy(harRv) === null) return null;
  const vrp = parseDecimalString(harRv?.variance_risk_premium);
  if (vrp === null) return null;
  const vrpPoints = vrp * 100;
  const meaning = vrp > 0
    ? "premium over-prices the model-estimated move"
    : vrp < 0
      ? "premium under-prices the model-estimated move"
      : "premium matches the model-estimated move";
  return `VRP ${vrpPoints > 0 ? "+" : ""}${vrpPoints.toFixed(1)}pt — ${meaning}.`;
}

// The honest absence note for a priced entry whose har_rv group is absent --
// never a fabricated forecast, never a blank cell.
export const HAR_RV_NOT_AVAILABLE = "Realized-vol model estimate not available";

// ---------------------------------------------------------------------------
// JS-1066: the ENERGY-lane composite badge. Mirrors the Signal meter's own
// "RATING, not a probability" framing (ADR-009 / ADR-011) EXACTLY: a bare
// 0-100 integer, NEVER a "%" sign. `energy_score_snapshot.composite` is a
// fraction in [0, 1] (same convention as `best_idea_score`), so this reuses
// the SAME round-to-0-100 math the Signal meter already applies, rather than
// growing a second, subtly-different rounding rule.

export const ENERGY_SIGNAL_NAMES = [
  "flow_zscore",
  "volume_oi_ratio",
  "rvol",
  "squeeze_percentile",
  "iv_extreme",
  "catalyst_proximity",
] as const;

export const ENERGY_SIGNAL_SHORT_LABELS: Record<string, string> = {
  flow_zscore: "Flow z-score",
  volume_oi_ratio: "Volume / OI",
  rvol: "Relative volume",
  squeeze_percentile: "Squeeze",
  iv_extreme: "IV extreme",
  catalyst_proximity: "Catalyst proximity",
};

export function energySignalLabel(name: string): string {
  return ENERGY_SIGNAL_SHORT_LABELS[name] ?? humanizeToken(name);
}

// A RATING in [0, 1] -> a bare 0-100 integer, or null on missing/unparseable
// input. Shared by the composite and every per-sub-signal score so the
// "0-100, no %" framing is applied identically everywhere on this badge.
export function energyRatingValue(fraction: string | null | undefined): number | null {
  const parsed = parseDecimalString(fraction);
  if (parsed === null) return null;
  return Math.round(Math.max(0, Math.min(1, parsed)) * 100);
}

// "3/6 signals available" -- verbatim off `signals_available`, never
// re-derived client-side (single source of truth, matches EnergyScore's own
// `coverage_label()` on the backend). Null when the count itself is absent.
export function energySignalCoverageLabel(
  signalsAvailable: number | null | undefined,
): string | null {
  if (signalsAvailable == null) return null;
  return `${signalsAvailable}/${ENERGY_SIGNAL_NAMES.length} signals available`;
}

// ---------------------------------------------------------------------------
// JS-1060: the edition's regime line -- a pure, deterministic FACTS-ONLY
// summary of edition.regime_summary (see
// domains.trade_ideas.edition.compute_regime_summary). No prose generation,
// no LLM call (ADR-011): every number here is read straight off the
// server-computed bucket counts. `by_iv_rv_regime` keys are the raw
// IV/RV regime codes (IMPLIED_RICH / IMPLIED_CHEAP / BALANCED /
// INSUFFICIENT_DATA); `by_archetype_family` keys are DEBIT / CREDIT_SHADOW
// (see the same module). Unrecognized/absent buckets are simply omitted from
// the line rather than fabricated as zero.

const IV_RV_REGIME_SHORT_LABELS: Record<string, string> = {
  IMPLIED_RICH: "IV-rich",
  IMPLIED_CHEAP: "IV-cheap",
  BALANCED: "balanced",
  INSUFFICIENT_DATA: "IV data insufficient",
};

const ARCHETYPE_FAMILY_SHORT_LABELS: Record<string, string> = {
  DEBIT: "debit",
  CREDIT_SHADOW: "credit",
};

// JS-1066: `by_discovery_lane` keys are the JS-1065 lane names -- "ENERGY"
// and "CORE_LIQUID" today (see energy_lane.py). A future/unmapped lane name
// still contributes via humanizeToken rather than being dropped.
const DISCOVERY_LANE_SHORT_LABELS: Record<string, string> = {
  CORE_LIQUID: "core",
  ENERGY: "energy",
};

function formatBucketClause(
  counts: Record<string, number> | null | undefined,
  labels: Record<string, string>,
  order: string[],
  options: { separator?: string; countFirst?: boolean } = {},
): string | null {
  if (!counts) return null;
  const { separator = " / ", countFirst = false } = options;
  const render = (key: string, count: number) => {
    const label = labels[key] ?? humanizeToken(key);
    return countFirst ? `${count} ${label}` : `${label} ${count}`;
  };
  const parts: string[] = [];
  const seen = new Set<string>();
  for (const key of order) {
    const count = counts[key];
    if (typeof count === "number" && count > 0) {
      parts.push(render(key, count));
      seen.add(key);
    }
  }
  // Any bucket the server reported that isn't in the preferred display order
  // still contributes (sorted by descending count, matching the server's own
  // `_bucket_counts` ordering) rather than being silently dropped.
  const rest = Object.entries(counts)
    .filter(([key, count]) => !seen.has(key) && count > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  for (const [key, count] of rest) {
    parts.push(render(key, count));
  }
  return parts.length > 0 ? parts.join(separator) : null;
}

/**
 * Build the edition masthead's deterministic regime line, e.g.
 * "12 curated ideas · IV-rich 7 / balanced 5 · 8 debit / 4 credit · 24 core · 6 energy".
 * Pure and total: an absent/zero regime_summary still renders an honest
 * "0 curated ideas" rather than throwing or omitting the line entirely.
 *
 * JS-1066: gains a trailing lane-count clause (e.g. "24 core · 6 energy")
 * whenever `by_discovery_lane` is present and non-empty -- i.e. the edition
 * was built from a run where the two-lane sweep actually ran (see
 * edition.compute_regime_summary's docstring: the backend OMITS this key
 * entirely, never a bare `{}`, when no lanes ran). Absent/empty lane data
 * renders the line EXACTLY as it did before this field existed -- no empty
 * trailing clause, no "0 core · 0 energy" fabrication.
 */
export function editionRegimeLine(
  regimeSummary: {
    idea_count?: number;
    by_iv_rv_regime?: Record<string, number>;
    by_iv_rank_band?: Record<string, number>;
    by_direction?: Record<string, number>;
    by_archetype_family?: Record<string, number>;
    by_discovery_lane?: Record<string, number>;
    market_regime?: MarketRegimeMastheadFacts | null;
  } | null | undefined,
): string {
  const ideaCount = regimeSummary?.idea_count ?? 0;
  const clauses = [`${ideaCount} curated ${ideaCount === 1 ? "idea" : "ideas"}`];

  // JS-1068: leads with the market-wide trend regime (when resolved) ahead
  // of every per-idea bucket below -- the market's own state is the widest-
  // scope fact on this line, matching the ticket's masthead-line framing.
  const marketRegimeClause = marketRegimeMastheadLine(regimeSummary?.market_regime);
  if (marketRegimeClause) clauses.push(marketRegimeClause);

  const regimeClause = formatBucketClause(
    regimeSummary?.by_iv_rv_regime,
    IV_RV_REGIME_SHORT_LABELS,
    ["IMPLIED_RICH", "BALANCED", "IMPLIED_CHEAP", "INSUFFICIENT_DATA"],
  );
  if (regimeClause) clauses.push(regimeClause);

  const familyClause = formatBucketClause(
    regimeSummary?.by_archetype_family,
    ARCHETYPE_FAMILY_SHORT_LABELS,
    ["DEBIT", "CREDIT_SHADOW"],
  );
  if (familyClause) clauses.push(familyClause);

  const laneClause = formatBucketClause(
    regimeSummary?.by_discovery_lane,
    DISCOVERY_LANE_SHORT_LABELS,
    ["CORE_LIQUID", "ENERGY"],
    { separator: " · ", countFirst: true },
  );
  if (laneClause) clauses.push(laneClause);

  return clauses.join(" · ");
}

// ---------------------------------------------------------------------------
// JS-1096 round 2 (Codex review fix -- MED finding 1 + 3): the running
// record's shared data/formatting primitive, extracted so BOTH the daily
// edition's full ScoreboardStrip card (EditionPage.tsx) AND the screener
// masthead's compact inline block (MultiStrategyScreenTable.tsx's
// DeskMasthead) derive the SAME won/lost/expired counts and the SAME
// provenance label from ONE implementation -- genuine extraction, never two
// components independently reading `scoreboard_delta` their own way.
//
// MED finding 1 (scoreboard provenance): `/editions/today` intentionally
// returns the latest PRIOR edition when today's has not been generated yet
// (see edition.py's EditionReadService.get_today), so a running record
// rendered with no date attached can present a stale count as current. The
// fix is NOT to hide the record (it is cumulative -- showing it is honest)
// but to always attach a compact "Edition #N · <date>" provenance label
// derived straight from the SAME edition payload the counts came from, so a
// reader can always tell which edition's snapshot they are looking at.
// ---------------------------------------------------------------------------

// Structural, not the full EditionDetail from contracts.ts -- this module
// stays free of a contracts.ts dependency (matching every other narrow
// candidate interface above, e.g. DisplayEntryCandidate).
export interface RunningRecordEditionCandidate {
  as_of?: string | null;
  edition_number?: number | null;
  scoreboard_delta?: {
    running_record?: Record<string, number> | null;
    running_record_total?: number | null;
    resolved_since_previous_count?: number | null;
  } | null;
}

export interface RunningRecordFacts {
  total: number;
  won: number;
  lost: number;
  expired: number;
  resolvedSincePrevious: number;
  /** Compact "Edition #N · <date>" provenance label -- ALWAYS present
   * (never null) whenever this record is derived from a real edition
   * payload, so a caller can never render the record without it. */
  editionLabel: string;
}

// Compact edition date, e.g. "Wed Sep 3" -- distinct from EditionPage.tsx's
// own `formatEditionDate` (full "Wednesday, September 3, 2026" for the page
// header), sized for a masthead provenance label instead, matching the
// prototype's comma-less "Wed Sep 3" masthead convention exactly (built from
// Intl parts rather than `toLocaleDateString`'s default punctuation, which
// inserts a locale-dependent comma after the weekday). `as_of` is a plain ET
// market date (YYYY-MM-DD); parsed as UTC-noon so no local-timezone
// rollback shifts the displayed calendar day (same technique
// `formatEditionDate` uses).
const COMPACT_EDITION_DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  weekday: "short",
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

function formatCompactEditionDate(asOf: string | null | undefined): string | null {
  if (!asOf) return null;
  const parsed = new Date(`${asOf}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return asOf;
  const parts = COMPACT_EDITION_DATE_FORMATTER.formatToParts(parsed);
  const byType = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value ?? "";
  const weekday = byType("weekday");
  const month = byType("month");
  const day = byType("day");
  if (!weekday || !month || !day) return asOf;
  return `${weekday} ${month} ${day}`;
}

/**
 * Derive the running-record counts + a mandatory provenance label from one
 * edition payload. Returns null only when there is no edition to derive
 * from at all (the caller's honest-absence gate, e.g. no edition access, no
 * edition published yet, or the query still failing) -- callers must never
 * synthesize a record without a real edition behind it.
 */
export function runningRecordFacts(
  edition: RunningRecordEditionCandidate | null | undefined,
): RunningRecordFacts | null {
  if (!edition) return null;
  const scoreboard = edition.scoreboard_delta;
  const record = scoreboard?.running_record ?? {};
  const editionNumberLabel = edition.edition_number != null ? `Edition #${edition.edition_number}` : "Edition";
  const dateLabel = formatCompactEditionDate(edition.as_of);
  const editionLabel = dateLabel ? `${editionNumberLabel} · ${dateLabel}` : editionNumberLabel;
  return {
    total: scoreboard?.running_record_total ?? 0,
    won: record.WON ?? 0,
    lost: record.LOST ?? 0,
    expired: record.EXPIRED ?? 0,
    resolvedSincePrevious: scoreboard?.resolved_since_previous_count ?? 0,
    editionLabel,
  };
}

// Generic fallback: SNAKE_CASE / camelCase → "Sentence case" so an unmapped
// code is still legible rather than shouting raw machine text.
export function humanizeToken(token: string): string {
  const spaced = token
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim()
    .toLowerCase();
  if (!spaced) return token;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// ---------------------------------------------------------------------------
// JS-1089 round 2: `hasEconomics` moved here (out of the component-only
// ThesisQuestionSections.tsx, which react-refresh/only-export-components
// flags for mixing a non-component export in with its components) --
// structural, DisplayEntryCandidate-shaped, matching this module's existing
// zero-dependency-on-contracts.ts convention.
// ---------------------------------------------------------------------------

export interface EconomicsPresenceCandidate {
  maximum_loss_usd?: string | null;
  maximum_profit_usd?: string | null;
  natural_entry_per_share?: string | null;
  [key: string]: unknown;
}

export function hasEconomics(entry: EconomicsPresenceCandidate): boolean {
  return entry.maximum_loss_usd != null || entry.maximum_profit_usd != null || entry.natural_entry_per_share != null;
}

// ---------------------------------------------------------------------------
// JS-1089 round 2 (FIX 2): the drawer's overlay (RS/Flow) views + derived
// "watch-out" reason codes, EXTRACTED from MultiStrategyScreenTable.tsx so
// the daily edition's expandable card can derive the SAME adverse-signal
// watch-outs (FLOW_CONTRADICTS / DIRECTION_RS_CONFLICT / EARNINGS_SOON) from
// its OWN frozen row, rather than silently omitting them. Sources ONLY row
// facts already present on the frozen snapshot (`relative_strength` /
// `options_flow` / `earnings.within_veto_window`) -- no re-derivation, no
// live re-fetch, so an archived edition's watch-outs stay exactly what the
// row said the day it was published.
// ---------------------------------------------------------------------------

export interface OverlayView {
  available: boolean;
  label: string | null;
  detail: string | null;
  tone: "confirm" | "conflict" | "neutral";
}

// Exported: also used standalone by MultiStrategyScreenTable's row (e.g.
// `overlayString(earnings, "next_earnings_date")`), independent of the
// RS/Flow views below -- one shared string-field reader, not a duplicate.
export function overlayString(overlay: Record<string, unknown> | null | undefined, key: string): string | null {
  if (!overlay) return null;
  const value = overlay[key];
  return typeof value === "string" && value.length > 0 ? value : null;
}

function overlayReasonCodes(overlay: Record<string, unknown> | null | undefined): string[] {
  if (!overlay) return [];
  const codes = overlay.reason_codes;
  return Array.isArray(codes) ? codes.filter((code): code is string => typeof code === "string") : [];
}

// Signed percentage from a decimal-string excess return (0.0123 → "+1.2%").
function signedPercent(value: string | null | undefined): string | null {
  if (value == null) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  const pct = parsed * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(1)}%`;
}

// Row shape both relativeStrengthView/optionsFlowView/deriveWatchOuts need --
// structural (matches this module's DisplayEntryCandidate/DisplayEntryRow
// convention above), not a contracts.ts import.
export interface WatchOutRow {
  relative_strength?: Record<string, unknown> | null;
  options_flow?: Record<string, unknown> | null;
  earnings?: Record<string, unknown> | null;
  [key: string]: unknown;
}

export function relativeStrengthView(row: WatchOutRow): OverlayView {
  const overlay = row.relative_strength;
  const available = overlay?.available !== false && Boolean(overlay);
  const label = overlayString(overlay, "label");
  const codes = overlayReasonCodes(overlay);
  const conflict = codes.includes("DIRECTION_RS_CONFLICT");
  const detail = signedPercent(overlayString(overlay, "excess_return"));
  return {
    available,
    label: label ? label.replace(/_/g, " ").toLowerCase() : null,
    detail,
    tone: conflict ? "conflict" : label === "OUTPERFORMING" ? "confirm" : "neutral",
  };
}

export function optionsFlowView(row: WatchOutRow): OverlayView {
  const overlay = row.options_flow;
  const available = overlay?.available !== false && Boolean(overlay);
  const label = overlayString(overlay, "label");
  const codes = overlayReasonCodes(overlay);
  const detail = overlayString(overlay, "ratio");
  let tone: OverlayView["tone"] = "neutral";
  if (codes.includes("FLOW_CONFIRMS")) tone = "confirm";
  else if (codes.includes("FLOW_CONTRADICTS")) tone = "conflict";
  return {
    available,
    label: label ? label.replace(/_/g, " ").toLowerCase() : null,
    detail: detail ? `${Number(detail).toFixed(2)}×` : null,
    tone,
  };
}

// The ONE place the drawer's "Why now" watch-outs (adverse-signal reason
// codes, distinct from the entry's own `reason_codes`) are derived from row
// facts -- returns raw reason codes (never pre-humanized), matching how
// `whyCodes` is handled at every call site (the caller applies
// `reasonCodeLabel`). Both MultiStrategyScreenTable's ThesisDrawer and
// EditionPage's idea-card expansion call this SAME function so an archived
// edition's watch-outs are never silently absent just because they were
// re-derived nowhere.
export function deriveWatchOutCodes(
  row: WatchOutRow,
  // DESK-009 (JS-1144), Codex adversarial-review HIGH (round 2): the
  // SELECTED entry's row-face disclosures.
  //
  // RCA: the disclosures were persisted but NO frontend surface read
  // `disclosure_reason_codes` at all, so negative EV, the cheap-vol waiver,
  // the rich-vol escape and an unresolved regime were invisible to the
  // trader -- which is the entire point of calling them ROW-FACE codes.
  //
  // They belong here rather than beside `abstain_reason_codes`: a watch-out
  // is precisely a non-veto caveat on an idea the desk IS showing, which is
  // exactly what a disclosure is. The existing watch-outs are row-level
  // signals; a disclosure is a fact about the selected EXPRESSION, so it is
  // passed in rather than re-derived from the row. Optional, so every
  // existing caller (and any archived edition with no disclosures)
  // decodes and renders exactly as before.
  entry?: { disclosure_reason_codes?: string[] | null } | null,
): string[] {
  const rs = relativeStrengthView(row);
  const flow = optionsFlowView(row);
  const earnings = row.earnings;
  const withinVeto = earnings?.within_veto_window === true;
  const codes: string[] = [];
  if (flow.tone === "conflict") codes.push("FLOW_CONTRADICTS");
  if (rs.tone === "conflict") codes.push("DIRECTION_RS_CONFLICT");
  if (withinVeto) codes.push("EARNINGS_SOON");
  for (const code of entry?.disclosure_reason_codes ?? []) {
    if (!codes.includes(code)) codes.push(code);
  }
  return codes;
}
